import { useState, useRef, useEffect, useCallback } from "react";
import { createRoot } from "react-dom/client";
import { PAGE_DEFS } from "../../data/pageTemplates";
import { apiFetch } from "../../api/api";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useCart } from "../../Context/Cartcontext";
import CollageCanvas from "./CollageCanvas";
import MobileEditorView from "./MobileEditorView";

function waitForImages(container) {
  const imgs = Array.from(container.querySelectorAll("img"));
  return Promise.all(
    imgs.map(img => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise(resolve => {
        img.addEventListener("load", resolve, { once: true });
        img.addEventListener("error", resolve, { once: true });
      });
    })
  );
}

function getPdfDimensionsMM(size) {
  console.log("🔍 getPdfDimensionsMM called with size:", size);
  const match = (size || "").match(/(\d+(\.\d+)?)"\s*x\s*(\d+(\.\d+)?)"/i);
  let wIn = 7, hIn = 7;
  if (match) {
    wIn = parseFloat(match[1]);
    hIn = parseFloat(match[3]);
  }
  const MM_PER_INCH = 25.4;
  const result = { wMM: wIn * MM_PER_INCH, hMM: hIn * MM_PER_INCH };
  console.log("🔍 Computed dimensions:", result);
  return result;
}

// naya helper — image URL ko base64 data URL mein convert karta hai
async function toDataURL(url) {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
  try {
    const proxyUrl = `https://aishcraft.com/backend/api/public/image-proxy.php?url=${encodeURIComponent(url)}`;
    const res = await fetch(proxyUrl);
    if (!res.ok) throw new Error("Proxy fetch failed: " + res.status);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn("🔍 toDataURL failed for", url, e);
    return url;
  }
}
// pages ke andar saari remote (http/https) images ko base64 mein resolve karo
async function preparePagesForPdf(pages) {
  const cache = new Map();
  const resolve = async (url) => {
    if (!url) return url;
    if (url.startsWith("blob:") || url.startsWith("data:")) return url;
    if (cache.has(url)) return cache.get(url);
    const dataUrl = await toDataURL(url);
    cache.set(url, dataUrl);
    return dataUrl;
  };



  return Promise.all(pages.map(async (pg) => {
    const [bgImage, backBgImage, frames] = await Promise.all([
      resolve(pg.bgImage),
      resolve(pg.backBgImage),
      Promise.all((pg.frames || []).map(async f => ({ ...f, image: await resolve(f.image) }))),
    ]);
    return { ...pg, bgImage, backBgImage, frames };
  }));
}

// async function generatePdfBlob(pages, projectName, size) {
//   console.log("🔍 START generatePdfBlob. pages:", pages.length);

//   const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
//     import("html2canvas"),
//     import("jspdf"),
//   ]);

//   if (document.fonts && document.fonts.ready) {
//     try {
//       await document.fonts.ready;
//     } catch (e) {
//       console.warn("🔍 document.fonts.ready failed:", e);
//     }
//   }

//   const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
//   const PW = 210, PH = 297;

//   const container = document.getElementById("pdf-render-container");
//   console.log("🔍 container found?", !!container);

//   // 👇 mobile pe canvas size limit crash se bachne ke liye scale kam rakho
//   const isMobileDevice = typeof window !== "undefined" && window.innerWidth < 768;
//   const canvasScale = isMobileDevice ? 1.5 : 3;

//   for (let i = 0; i < pages.length; i++) {
//     const el = container?.querySelector(`#pb-page-${pages[i].id}`);
//     console.log(`🔍 Page ${i} (id=${pages[i].id}): element found?`, !!el);
//     if (!el) { if (i > 0) pdf.addPage(); continue; }

//     console.log(`🔍 Page ${i}: offsetWidth/Height =`, el.offsetWidth, el.offsetHeight);

//     await waitForImages(el);

//     const canvas = await html2canvas(el, {
//       scale: canvasScale,
//       useCORS: true,
//       allowTaint: true,
//       backgroundColor: pages[i].bg || "#FFFFFF",
//       logging: false,
//       imageTimeout: 0,
//     });

//     console.log(`🔍 Page ${i}: canvas size =`, canvas.width, canvas.height);

//     const imgData = canvas.toDataURL("image/jpeg", 0.95);
//     console.log(`🔍 Page ${i}: imgData length =`, imgData.length);

//     const ratio = canvas.width / canvas.height;
//     let dW = PW, dH = PW / ratio;
//     if (dH > PH) { dH = PH; dW = PH * ratio; }

//     if (i > 0) pdf.addPage();
//     pdf.addImage(imgData, "JPEG", (PW - dW) / 2, (PH - dH) / 2, dW, dH);

//     if (i >= 2) { console.log("🔍 STOPPING early for debug after 3 pages worth of logs"); }
//   }

//   console.log("🔍 DONE, generating blob");
//   return pdf.output("blob");
// }
// 👇 BADLAV: function signature mein 4th parameter "containerEl" add hua
// Aur andar "document.getElementById('pdf-render-container')" ki jagah
// wahi "containerEl" parameter use ho raha hai jo humein pass kiya jayega.

// 👇 NAYA: PDF banate waqt un texts ko hata do jo user ne edit hi nahi kiye (default/placeholder text)
function stripDefaultTexts(pages) {
  return pages.map(pg => ({
    ...pg,
    texts: (pg.texts || []).filter(t => !t.isDefault),
  }));
}

async function generatePdfBlob(pages, projectName, size, containerEl) {
  console.log("🔍 START generatePdfBlob. pages:", pages.length);

  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import("html2canvas"),
    import("jspdf"),
  ]);

  if (document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch (e) {
      console.warn("🔍 document.fonts.ready failed:", e);
    }
  }

  const { wMM, hMM } = getPdfDimensionsMM(size);
  console.log("🔍 Target physical size (mm):", wMM, "x", hMM);

  const pdf = new jsPDF({
    orientation: wMM >= hMM ? "landscape" : "portrait",
    unit: "mm",
    format: [wMM, hMM],
  });
  const PW = wMM, PH = hMM;

  // 👇 NAYA: verify actual jsPDF page size turant banne ke baad
  console.log("🔍 jsPDF actual page size (mm):", pdf.internal.pageSize.getWidth(), "x", pdf.internal.pageSize.getHeight());

  const container = containerEl;
  const isMobileDevice = typeof window !== "undefined" && window.innerWidth < 768;

  // 👇 NAYA: 300 DPI target ke hisaab se canvasScale dynamically nikalo
  const TARGET_DPI = 300;
  const wInches = wMM / 25.4;
  const desiredPixelWidth = wInches * TARGET_DPI; // e.g. 2100 for mini, 3000 for medium

  for (let i = 0; i < pages.length; i++) {
    const el = container?.querySelector(`#pb-page-${pages[i].id}`);
    if (!el) { if (i > 0) pdf.addPage([PW, PH]); continue; }

    await waitForImages(el);

    // 👇 NAYA: is page ke actual CSS width ke against scale calculate karo
    const cssWidth = el.offsetWidth || 800;
    let canvasScale = desiredPixelWidth / cssWidth;
    if (isMobileDevice) canvasScale = Math.min(canvasScale, 2); // mobile crash-safety cap

    console.log(`🔍 Page ${i}: cssWidth=${cssWidth}, canvasScale=${canvasScale.toFixed(2)}, targetPx=${Math.round(cssWidth*canvasScale)}`);

    const canvas = await html2canvas(el, {
      scale: canvasScale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: pages[i].bg || "#FFFFFF",
      logging: false,
      imageTimeout: 0,
    });

    console.log(`🔍 Page ${i}: final canvas px =`, canvas.width, "x", canvas.height);

   const imgData = canvas.toDataURL("image/jpeg", 0.82);
    const ratio = canvas.width / canvas.height;
    let dW = PW, dH = PW / ratio;
    if (dH > PH) { dH = PH; dW = PH * ratio; }

    if (i > 0) pdf.addPage([PW, PH]);
    pdf.addImage(imgData, "JPEG", (PW - dW) / 2, (PH - dH) / 2, dW, dH);
  }

  console.log("🔍 DONE, generating blob");
  return pdf.output("blob");
}


// 👇 NAYA: end me "Add Page" dabane pe last page jaisa layout repeat karke 3 pages ek sath add karta hai

// async function generatePdf(pages, projectName) {
//   await new Promise(resolve => setTimeout(resolve, 500));

//   const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
//     import("html2canvas"),
//     import("jspdf"),
//   ]);

//   const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
//   const PW = 297, PH = 210;

//   for (let i = 0; i < pages.length; i++) {
//     const el = document.getElementById(`pb-page-${pages[i].id}`);
//     if (!el) { if (i > 0) pdf.addPage(); continue; }

//     const canvas = await html2canvas(el, {
//       scale: 2.5,
//       useCORS: true,
//       allowTaint: true,
//       backgroundColor: pages[i].bg || "#FFFFFF",
//       logging: false,
//       imageTimeout: 0,
//     });

//     const imgData = canvas.toDataURL("image/jpeg", 0.95);
//     const ratio = canvas.width / canvas.height;
//     let dW = PW, dH = PW / ratio;
//     if (dH > PH) { dH = PH; dW = PH * ratio; }

//     if (i > 0) pdf.addPage();
//     pdf.addImage(imgData, "JPEG", (PW - dW) / 2, (PH - dH) / 2, dW, dH);
//   }

//   pdf.save(`${projectName || "photobook"}.pdf`);

//   try {
//     const prev = JSON.parse(localStorage.getItem("aishcraft_pdf_orders") || "[]");
//     const order = {
//       id:       `PDF${Date.now()}`,
//       orderId:  `#AC${Math.floor(10000 + Math.random() * 90000)}`,
//       customer: "Customer",
//       email:    "",
//       product:  projectName,
//       pages:    pages.length,
//       date:     new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
//       status:   "Pending",
//       size:     `~${(pages.length * 1.8).toFixed(1)} MB`,
//     };
//     localStorage.setItem("aishcraft_pdf_orders", JSON.stringify([order, ...prev]));
//   } catch {}
// }

const FONTS = ["Inter","Playfair Display","Cormorant Garamond","Lato","Montserrat","Georgia","Times New Roman"];
const FONT_SIZES = ["10px","12px","14px","16px","18px","20px","24px","28px","32px","36px","42px","48px"];
const BG_COLORS = ["#FFFDF7","#F5F0E8","#FFFFFF","#F2F1ED","#1A1A1A","#2D2D2D","#F0E6D3","#D4E8D4","#E8D4D4","#D4D4E8"];

const MOTHERHOOD_SLUG = "motherhood-memory-album";

// 👇 Ek photo-frame set ka "signature" banata hai (x/y/w/h ke round-off values se)
// isse hum backend template ke pages ko unke actual layout ke hisaab se group/match kar sakte hain
const buildLayoutSignature = (photos) =>
  (photos || [])
    .map(ph => `${Math.round(ph.x)}_${Math.round(ph.y)}_${Math.round(ph.w)}_${Math.round(ph.h)}`)
    .sort()
    .join("|");

const pagesFromTemplate = (tplPages) => {
  const buildFrames = (photos) => (photos || []).map((ph, fi) => ({
    id: ph.id || fi,
    x: ph.x, y: ph.y, w: ph.w, h: ph.h,
    rotation: ph.rotate || 0,
    image: null, imgOffset: { x: 0, y: 0 }, imgZoom: 1,
    color: "#E0E0E0", caption: "", noCaption: true,
  }));
  const buildTexts = (texts, isCoverText = false) => (texts || []).map((t, ti) => ({
    id: t.id || ti + 1, content: t.content || "",
    x: t.x, y: t.y, w: t.w,
    font: t.font || "Georgia", size: t.size || "16px",
    color: t.color || "#1A1A1A", align: t.align || "center",
    // 👇 Cover text hamesha PDF mein aayega (default ho ya user-typed).
    // Normal page text tabhi aayega jab user khud type kare.
    isDefault: isCoverText ? false : true,
  }));

  const backCoverTpl  = tplPages.find(p => p.coverSide === "back");
  const frontCoverTpl = tplPages.find(p => p.coverSide === "front");
  const normalTpls    = tplPages.filter(p => p.coverSide !== "back" && p.coverSide !== "front");

  const coverPage = {
    id: 0, isCover: true, defId: "cover",
    bg: frontCoverTpl?.bgColor || "#D8D5D0",
    bgImage: frontCoverTpl?.bgImageUrl || null,
    backBg: backCoverTpl?.bgColor || "#D8D5D0",
    backBgImage: backCoverTpl?.bgImageUrl || null,
    frames: buildFrames(frontCoverTpl?.photos),
    texts: buildTexts(frontCoverTpl?.texts, true),
  };

  // 👇 Backend template se hi unique layout definitions banao (static PAGE_DEFS file use nahi hogi is mode mein)
  const seen = new Map(); // signature -> defId
  const layoutDefsFromTpl = [];
  normalTpls.forEach((p) => {
    const sig = buildLayoutSignature(p.photos);
    if (!seen.has(sig)) {
      const defId = `tpl-layout-${seen.size}`;
      seen.set(sig, defId);
      layoutDefsFromTpl.push({
        id: defId,
        photoFrames: (p.photos || []).map(ph => ({ x: ph.x, y: ph.y, w: ph.w, h: ph.h })),
      });
    }
  });

  const normalPages = normalTpls.map((p, i) => {
    const frames = buildFrames(p.photos);
    const sig = buildLayoutSignature(p.photos);
    return {
      id: Date.now() + i, isCover: false,
      bg: p.bgColor || "#FFFFFF",
      bgImage: p.bgImageUrl || null,
      defId: seen.get(sig) || null,
      frames,
        texts: buildTexts(p.texts),  
    };
  });

  return { pages: [coverPage, ...normalPages], layoutDefs: layoutDefsFromTpl };
};

function Tip({ label, children, side = "bottom" }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative flex items-center justify-center"
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div className={`absolute z-[300] whitespace-nowrap bg-gray-800 text-white text-[10px] px-2 py-1 rounded pointer-events-none
          ${side==="bottom"?"top-full mt-1.5":side==="left"?"right-full mr-2":"left-full ml-2"}`}>
          {label}
        </div>
      )}
    </div>
  );
}

const makeDefaultPages = () => {
  const cover = { id: 0, isCover: true, defId: "cover", bg: "#D8D5D0", backBg: "#D8D5D0", frames: [], texts: [] };
  const normalDefs = PAGE_DEFS.filter(d => d.id !== "cover");
  const TOTAL_PAGES = 24;
  const normalPages = Array.from({ length: TOTAL_PAGES }, (_, i) => {
    const def = normalDefs[i % normalDefs.length];
    return {
      id: Date.now() + i + 1,
      isCover: false,
      bg: "#FFFFFF",
      defId: def.id,
      frames: def.photoFrames.map((pf, fi) => ({
        id: fi,
        ...pf,
        image: null,
        color: ["#D8D5D0","#CFCBC6","#D0CCCA","#C8C4C0"][fi % 4],
        caption: "",
      })),
      texts: [],
    };
  });
  return [cover, ...normalPages];
};

function EndFocusTextarea({ value, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) {
      const len = el.value.length;
      el.focus();
      el.setSelectionRange(len, len);
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
    }
  }, []); // eslint-disable-line
  return (
    <textarea
      ref={ref}
      value={value}
      onInput={e => {
        e.target.style.height = "auto";
        e.target.style.height = e.target.scrollHeight + "px";
      }}
      {...props}
    />
  );
} 

function EndFocusInput({ value, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) {
      const len = el.value.length;
      el.focus();
      el.setSelectionRange(len, len);
    }
  }, []); // eslint-disable-line
  return <input ref={ref} value={value} {...props} />;
}

function TextPopup({ value, onSave, onCancel, font, size, align }) {
  const [val, setVal] = useState(value || "");
  return (
    <div className="fixed inset-0 bg-black/30 z-[200] flex items-center justify-center" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-[520px] mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 border-b border-gray-100">
          <span className="text-sm font-medium text-gray-700">Edit Text</span>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-700">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="p-4 sm:p-5">

     <EndFocusTextarea value={val} onChange={e => setVal(e.target.value)}
  placeholder="Add photo caption here or delete if you prefer"
  className="w-full rounded-xl p-3 text-sm text-gray-700 outline-none resize-none transition-colors"
  style={{ minHeight: 200, fontFamily: font, fontSize: size, textAlign: align, border: "3px double #000" }}/>
        </div>
        <div className="flex justify-end gap-2 px-4 sm:px-6 py-3.5 sm:py-4 border-t border-gray-100">
          <button onClick={onCancel} className="flex items-center gap-1.5 px-4 sm:px-5 py-2 border border-gray-300 text-gray-600 text-sm rounded-xl hover:bg-gray-50 transition-colors">
            Cancel <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3 h-3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <button onClick={() => onSave(val)} className="flex items-center gap-1.5 px-4 sm:px-5 py-2 bg-gray-700 text-white text-sm rounded-xl hover:bg-gray-900 transition-colors">
            OK <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3 h-3"><polyline points="20 6 9 17 4 12"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function PhotoFrame({ frame, selected, onPhotoClick, onCaptionClick, onDrop, onUpdate, readonly }) {
  const captionH = frame.noCaption ? 0 : 18;
  const photoH = 100 - captionH;
  const [hover, setHover] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [naturalSize, setNaturalSize] = useState(null);
  const dragStart = useRef(null);
  const boxRef = useRef(null);
  const imgRef = useRef(null);

  const imgOffset = frame.imgOffset || { x: 0, y: 0 };
  const imgZoom = frame.imgZoom || 1;
  const filter = frame.filter || "none";
  const filterStyle = filter === "grayscale" ? "grayscale(100%)" : filter === "sepia" ? "sepia(80%)" : filter === "brightness" ? "brightness(1.3) contrast(0.9)" : "none";

  const handleImgLoad = (e) => setNaturalSize({ w: e.target.naturalWidth, h: e.target.naturalHeight });

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth) {
      setNaturalSize({ w: imgRef.current.naturalWidth, h: imgRef.current.naturalHeight });
    }
  }, [frame.image]);

const getLayout = () => {
  const box = boxRef.current;
  if (!box || !naturalSize) return null;
  const cw = box.offsetWidth;
  const ch = box.offsetHeight;
  const baseScale = Math.max(cw / naturalSize.w, ch / naturalSize.h); // "cover" ke liye minimum scale
  const scale = baseScale * imgZoom;
  const dispW = naturalSize.w * scale;
  const dispH = naturalSize.h * scale;
  const maxX = Math.max(0, (dispW - cw) / 2);
  const maxY = Math.max(0, (dispH - ch) / 2);
  return { dispW, dispH, maxX, maxY };
};

  const clampOffset = (x, y) => {
    const layout = getLayout();
    if (!layout) return { x, y };
    return {
      x: Math.min(layout.maxX, Math.max(-layout.maxX, x)),
      y: Math.min(layout.maxY, Math.max(-layout.maxY, y)),
    };
  };

  const handleMouseDown = (e) => {
    if (!frame.image || readonly) return;
    e.preventDefault();
    setDragging(true);
    dragStart.current = { x: e.clientX - imgOffset.x, y: e.clientY - imgOffset.y };
  };
  const handleMouseMove = (e) => {
    if (!dragging || !dragStart.current) return;
    const raw = { x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y };
    onUpdate && onUpdate({ imgOffset: clampOffset(raw.x, raw.y) });
  };
const handleMouseUp = () => { setDragging(false); dragStart.current = null; };

// 👇 NAYA — mobile touch support
const handleTouchStart = (e) => {
  if (!frame.image || readonly) return;
  const t = e.touches[0];
  setDragging(true);
  dragStart.current = { x: t.clientX - imgOffset.x, y: t.clientY - imgOffset.y };
};
const handleTouchMove = (e) => {
  if (!dragging || !dragStart.current) return;
  e.preventDefault();
  const t = e.touches[0];
  const raw = { x: t.clientX - dragStart.current.x, y: t.clientY - dragStart.current.y };
  onUpdate && onUpdate({ imgOffset: clampOffset(raw.x, raw.y) });
};
const handleTouchEnd = () => { setDragging(false); dragStart.current = null; };

  const layout = getLayout();

  return (
<div data-frame-id={frame.id} className="absolute" style={{ left:`${frame.x}%`, top:`${frame.y}%`, width:`${frame.w}%`, height:`${frame.h}%` }}
  onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
      {!readonly && <div className="absolute inset-0 pointer-events-none z-10" style={{ border:"1px dashed #ccc" }}/>}
      {selected && !readonly && <div className="absolute pointer-events-none z-20" style={{ inset:-3, border:"3px double #000" }}/>}
   <div ref={boxRef} className="absolute overflow-hidden"
  style={{ left:0, top:0, width:"100%", height:`${photoH}%`, cursor: frame.image ? (dragging?"grabbing":"grab") : "pointer", touchAction: "none" }}
  onClick={() => !readonly && onPhotoClick && onPhotoClick()}
  onMouseDown={handleMouseDown}
  onMouseEnter={() => setHover(true)}
  onMouseLeave={() => setHover(false)}
  onTouchStart={handleTouchStart}
  onTouchMove={handleTouchMove}
  onTouchEnd={handleTouchEnd}
  onDragOver={e => e.preventDefault()}
  onDrop={e => { e.preventDefault(); if (!readonly && onDrop) { const url = e.dataTransfer.getData("imageUrl"); onDrop(url ? null : e.dataTransfer.files[0], url || null); } }}>
        {frame.image ? (
          <>
            <img
              ref={imgRef}
              src={frame.image}
              alt=""
              draggable={false}
              onLoad={handleImgLoad}
              className="absolute select-none"
              style={
                layout
                  ? {
                      left: "50%", top: "50%",
                      width: layout.dispW, height: layout.dispH,
                      transform: `translate(-50%,-50%) translate(${imgOffset.x}px,${imgOffset.y}px) rotate(${frame.rotation||0}deg) scaleX(${frame.flipH?-1:1})`,
                      filter: filterStyle,
                    }
                  : { width: "100%", height: "100%", objectFit: "cover", filter: filterStyle }
              }
            />
            {hover && !dragging && !selected && (
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none">
                <div className="bg-black/60 text-white text-[9px] px-2 py-1 rounded-full flex items-center gap-1">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3 h-3"><path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3" strokeLinecap="round"/></svg>
                  Drag to adjust cropping
                </div>
              </div>
            )}
            {selected && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-8 h-8 rounded-full bg-white/80 shadow flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" className="w-4 h-4"><path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3" strokeLinecap="round"/></svg>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: frame.color }}>
            {!readonly && <svg viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.2" className="w-5 h-5 opacity-60"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>}
          </div>
        )}
      </div>
      {!readonly && !frame.noCaption && (
        <div className="absolute cursor-pointer flex items-center justify-center"
          style={{ left:0, top:`${photoH}%`, width:"100%", height:`${captionH}%`, background:"#f5f5f3", borderTop:"1px dashed #ccc" }}
          onClick={() => onCaptionClick && onCaptionClick()}>
          {frame.caption
            ? <span className="text-[9px] text-gray-600 px-1 text-center leading-tight line-clamp-2" style={{ maxWidth:"90%" }}>{frame.caption}</span>
            : <div className="flex gap-1 items-center"><div className="w-1.5 h-1.5 rounded-full bg-gray-400"/><div className="w-1.5 h-1.5 rounded-full bg-gray-400"/><div className="w-1.5 h-1.5 rounded-full bg-gray-400"/></div>
          }
        </div>
      )}
    </div>
  );
}

function PageCanvas({ page, selectedFrame, onPhotoClick, onCaptionClick, onFrameDrop, onFrameUpdate, editingText, onTextDblClick, onTextChange, onTextBlur, readonly }) {
  return (
    <div id={`pb-page-${page.id}`} className="relative w-full h-full overflow-hidden select-none" style={{ background: page.bg }}
      onClick={e => { if (e.target === e.currentTarget && !readonly) onPhotoClick && onPhotoClick(null); }}>
      {page.bgImage && <img src={page.bgImage} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />}
      {page.frames.map(f => (
        <PhotoFrame key={f.id} frame={f} selected={selectedFrame === f.id}
          onPhotoClick={() => onPhotoClick && onPhotoClick(f.id)}
          onCaptionClick={() => onCaptionClick && onCaptionClick(f.id)}
          onDrop={(file, url) => onFrameDrop && onFrameDrop(f.id, file, undefined, url)}
          onUpdate={(props) => onFrameUpdate && onFrameUpdate(f.id, props)}
          readonly={readonly}/>
      ))}
{(page.texts || []).map(t => (
  <div key={t.id} className="absolute" style={{ left: `${t.x}%`, top: `${t.y}%`, width: `${t.w}%` }}>
    {!readonly && editingText === t.id ? (
<EndFocusTextarea value={t.content}
  onChange={e => onTextChange && onTextChange(t.id, e.target.value)}
  onBlur={() => onTextBlur && onTextBlur()}
  rows={1}
  className="w-full bg-transparent outline-none resize-none"
  style={{
    fontFamily: t.font,
    fontSize: t.size,
    color: t.color,
    textAlign: t.align || "left",
    border: "3px double #000",
    height: "auto",
    minHeight: 0,
    lineHeight: 1.2,
    padding: "2px 4px",
    boxSizing: "border-box",
    display: "block",
    overflow: "hidden",
  }}/>
    ) : (
  <div onClick={() => !readonly && onTextDblClick && onTextDblClick(t.id)} className="cursor-pointer whitespace-pre-line"
        style={{
          fontFamily: t.font, fontSize: t.size, color: t.color, textAlign: t.align || "left",
          border: readonly ? "none" : "1px dashed #ccc",
        }}>
        {t.content}
      </div>
    )}
  </div>
))}
    </div>
  );
}

function CoverSpread({ page, selectedFrame, editingCoverText, onPhotoClick, onCaptionClick, onFrameDrop, onFrameUpdate, onCoverTextDblClick, onCoverTextChange, onCoverTextBlur, readonly }) {
  return (
  <div className="flex h-full" onClick={e => { if (e.target === e.currentTarget && !readonly) onPhotoClick && onPhotoClick(null); }}>
      <div className="flex-1 relative overflow-hidden rounded-l-sm" style={{ background: page.backBg || "#FFFDF7" }}>
        {page.backBgImage && <img src={page.backBgImage} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute bottom-4 left-5 text-[11px] italic text-gray-400" style={{ fontFamily:"Georgia,serif", zIndex: 2 }}>Aishcraft</div>
      </div>
     <div className="w-5 flex-shrink-0 flex items-center justify-center" style={{ background:"#E8E8E8" }}>
  <span className="text-[6px] text-gray-400 tracking-widest uppercase" style={{ writingMode:"vertical-rl" }}>TEXT</span>
</div>
     <div id={`pb-page-${page.id}`} className="flex-1 relative overflow-hidden rounded-r-sm" style={{ background: page.bg }}
        onClick={e => { if (e.target === e.currentTarget && !readonly) onPhotoClick && onPhotoClick(null); }}>
        {page.bgImage && <img src={page.bgImage} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        {page.frames.map(f => (
          <PhotoFrame key={f.id} frame={f} selected={selectedFrame === f.id}
            onPhotoClick={() => onPhotoClick && onPhotoClick(f.id)}
            onCaptionClick={() => onCaptionClick && onCaptionClick(f.id)}
            onDrop={(file, url) => onFrameDrop && onFrameDrop(f.id, file, undefined, url)}
            onUpdate={(props) => onFrameUpdate && onFrameUpdate(f.id, props)}
            readonly={readonly}/>
        ))}
{(page.texts || []).map(t => (
  <div key={t.id} className="absolute" style={{ left: `${t.x}%`, top: `${t.y}%`, width: `${t.w}%` }}>
  {!readonly && editingCoverText === t.id ? (
<EndFocusInput type="text" value={t.content}
    onChange={e => onCoverTextChange && onCoverTextChange(t.id, e.target.value)}
    onBlur={() => onCoverTextBlur && onCoverTextBlur()}
    className="w-full bg-transparent outline-none"
   style={{
  fontFamily: t.font, fontSize: t.size, color: t.color, textAlign: t.align,
  border: "3px double #000",
}}/>
) : (
   <div onClick={() => !readonly && onCoverTextDblClick && onCoverTextDblClick(t.id)}
        className="cursor-pointer whitespace-pre-line"
       style={{
         fontFamily: t.font, fontSize: t.size, color: t.color, textAlign: t.align || "left",
         border: readonly ? "none" : "1px dashed #ccc",
       }}>
        {t.content}
      </div>
    )}
  </div>
))}
      </div>
    </div>
  );
}
function renderPageForPdf(pg) {
  const noop = () => {};
 
  if (pg.isCollage) return <CollageCanvas page={pg} selectedFrame={null} editingText={null} onPhotoClick={noop} onFrameDrop={noop} onFrameUpdate={noop} onTextDblClick={noop} onTextChange={noop} onTextBlur={noop} readonly />;
  if (pg.isCover) return <CoverSpread page={pg} selectedFrame={null} editingCoverText={null} onPhotoClick={noop} onCaptionClick={noop} onFrameDrop={noop} onCoverTextDblClick={noop} onCoverTextChange={noop} onCoverTextBlur={noop} readonly />;
  return <PageCanvas page={pg} selectedFrame={null} onPhotoClick={noop} onCaptionClick={noop} onFrameDrop={noop} onFrameUpdate={noop} readonly />;
}

// ── Generic renderer used both for left/right spread pages and the single page ──
function renderPolaroidPage(pg, opts) {
  const {
    selectedFrame, editingCoverText, onPhotoClick, onFrameDrop, onFrameUpdate,
    onTextDblClickSingle, onTextChangeSingle, onTextDblClickMulti, onTextChangeMulti,
    onTextBlur, onCaptionClick,
  } = opts;

  const commonSingle = {
    page: pg,
    selectedFrame,
    editingText: editingCoverText,
    onPhotoClick,
    onFrameDrop,
    onFrameUpdate,
    onTextDblClick: onTextDblClickMulti,
    onTextChange: onTextChangeMulti,
    onTextBlur,
  };
  const noText = { page: pg, selectedFrame, onPhotoClick, onFrameDrop, onFrameUpdate };

  if (pg.isPolaroidV18) return <PolaroidPageCanvasV18 {...commonSingle} />;
  if (pg.isPolaroidV19) return <PolaroidPageCanvasV19 {...commonSingle} />;
  if (pg.isPolaroidV20) return <PolaroidPageCanvasV20 {...commonSingle} />;
  if (pg.isPolaroidV21) return <PolaroidPageCanvasV21 {...commonSingle} />;
  if (pg.isPolaroidV22) return <PolaroidPageCanvasV22 {...commonSingle} />;
  if (pg.isPolaroidV23) return <PolaroidPageCanvasV23 {...commonSingle} />;
  if (pg.isPolaroidV24) return <PolaroidPageCanvasV24 {...commonSingle} />;
  if (pg.isPolaroidV25) return <PolaroidPageCanvasV25 {...noText} />;
  if (pg.isPolaroidV26) return <PolaroidPageCanvasV26 {...commonSingle} />;
  if (pg.isPolaroidV27) return <PolaroidPageCanvasV27 {...commonSingle} />;
  if (pg.isPolaroidV28) return <PolaroidPageCanvasV28 {...commonSingle} />;
  if (pg.isPolaroidV29) return <PolaroidPageCanvasV29 {...noText} />;
  if (pg.isPolaroidV30) return <PolaroidPageCanvasV30 {...commonSingle} />;
  if (pg.isPolaroidV31) return <PolaroidPageCanvasV31 {...commonSingle} />;
  if (pg.isPolaroidV32) return <PolaroidPageCanvasV32 {...commonSingle} />;
  if (pg.isPolaroidV33) return <PolaroidPageCanvasV33 {...commonSingle} />;
  if (pg.isPolaroidV34) return <PolaroidPageCanvasV34 {...noText} />;
  if (pg.isPolaroidV37) return <PolaroidPageCanvasV37 {...commonSingle} />;
  if (pg.isPolaroidV38) return <PolaroidPageCanvasV38 {...commonSingle} />;
  if (pg.isPolaroidV40) return <PolaroidPageCanvasV40 {...noText} />;
  return null;
}

export default function PhotobookEditor({ productColor = "#D8D5D0" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToCart, updateCartItem } = useCart();
const { starterMode, starterTemplateId, productSlug, productPrice, productImage, productName, size } = location.state || {};
  const { slug } = useParams();
  // const [pages, setPages] = useState(() => {
  //   if (slug === MOTHERHOOD_SLUG) return makeMotherhoodPages(productColor);
  //   return makeDefaultPolaroidPages(productColor);
  // });
const [pages, setPages] = useState(() => makeDefaultPages());
  const [starterLoaded, setStarterLoaded] = useState(false);
  const [cur, setCur] = useState(0);
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [activePage, setActivePage] = useState("left");
  const [captionPopup, setCaptionPopup] = useState(null);
  const [editingCoverText, setEditingCoverText] = useState(null);
   const [selectedTextId, setSelectedTextId] = useState(null); 
  const [activePanel, setActivePanel] = useState(null);
  const [projectName, setProjectName] = useState("My Project");
  const [editingName, setEditingName] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [saveStatus, setSaveStatus] = useState("saved");
  const [showSave, setShowSave] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showCheck, setShowCheck] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showOrganize, setShowOrganize] = useState(false);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [rightOpen, setRightOpen] = useState(false);
  const [captionFont, setCaptionFont] = useState("Inter");
  const [captionAlign, setCaptionAlign] = useState("center");
  const [uploadedImages, setUploadedImages] = useState([]);
  const [gallerySize, setGallerySize] = useState("md");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [mobileLeftOpen, setMobileLeftOpen] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const fileRef = useRef();
  const fillTargetRef = useRef(null); 



  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState("");
  const [productData, setProductData] = useState(null);
  const [pdfRenderPages, setPdfRenderPages] = useState(null);

  // 👇 Layouts panel isi state se render hota hai — default mode mein static PAGE_DEFS,
  // backend-template mode mein backend se derive kiye hue layouts
  const [layoutDefs, setLayoutDefs] = useState(() => PAGE_DEFS.filter(d => d.id !== "cover"));
  const [lockedPageIds, setLockedPageIds] = useState(() => new Set(pages.map(p => p.id)));

  const page = pages[cur];

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768) setZoom(48);
  }, []);

  const pushHistory = useCallback((next) => {
    setHistory(h => [...h.slice(-30), pages]);
    setFuture([]);
    setPages(next);
    setSaveStatus("saving");
    setTimeout(() => setSaveStatus("saved"), 1200);
  }, [pages]);

  const undo = () => { if (!history.length) return; setFuture(f => [pages, ...f]); setPages(history[history.length - 1]); setHistory(h => h.slice(0, -1)); };
  const redo = () => { if (!future.length) return; setHistory(h => [...h, pages]); setPages(future[0]); setFuture(f => f.slice(1)); };

  useEffect(() => {
    const h = e => {
      if (e.ctrlKey && e.key === "z") { e.preventDefault(); undo(); }
      if (e.ctrlKey && e.shiftKey && e.key === "Z") { e.preventDefault(); redo(); }
      if (e.ctrlKey && e.key === "s") { e.preventDefault(); setShowSave(true); }
if (e.key === "Escape") { setSelectedFrame(null); setSelectedTextId(null); setCaptionPopup(null); setShowOrganize(false); setShowTemplates(false); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  // 👇 NAYA: kahin bhi bahar click/tap karne pe selected frame ka pill hat jayega
  // (file-picker/Replace/Upload button ke click ko yahan se exclude kiya gaya hai,
  // warna photo replace karte waqt selection beech me hi hat jaati thi)
  useEffect(() => {
    if (selectedFrame === null) return;
    const handleOutside = (e) => {
      if (e.target.closest('input[type="file"]')) return;
      if (e.target.closest('[data-selection-pill]')) return;
      if (e.target.closest('[data-mobile-tool-panel]')) return;
      if (e.target.closest(`[data-frame-id="${selectedFrame}"]`)) return;
      setSelectedFrame(null);
    };
    document.addEventListener("click", handleOutside);
    return () => document.removeEventListener("click", handleOutside);
  }, [selectedFrame]);

useEffect(() => {
  if (!slug) {
    return;
  }

  apiFetch(`/public/product.php?slug=${slug}`)
    .then(res => {
      const product = res.data;
      setProductData(product);

      if (product.photobookStarterMode === "template" && product.photobookTemplateId) {
        return apiFetch(`/photobook/get.php?id=${product.photobookTemplateId}`)
          .then(tplRes => {
            const tpl = tplRes.data;
            const { pages: tplPages, layoutDefs: tplLayoutDefs } = pagesFromTemplate(tpl.pages);
            setPages(tplPages);
            setLockedPageIds(new Set(tplPages.map(p => p.id)));   // ✅ ye pehle se hai — SAHI
            setLayoutDefs(tplLayoutDefs.length ? tplLayoutDefs : PAGE_DEFS.filter(d => d.id !== "cover"));
          })
          .catch(err => {
            console.error("Template load failed, falling back to default layout:", err);
            const dp = makeDefaultPages();          // ❌ YE MISSING HAI
            setPages(dp);
            setLockedPageIds(new Set(dp.map(p => p.id)));   // ❌ YE LINE ADD KARO
            setLayoutDefs(PAGE_DEFS.filter(d => d.id !== "cover"));
          });
      } else {
        const dp = makeDefaultPages();               // ❌ YE MISSING HAI
        setPages(dp);
        setLockedPageIds(new Set(dp.map(p => p.id)));   // ❌ YE LINE ADD KARO
        setLayoutDefs(PAGE_DEFS.filter(d => d.id !== "cover"));
      }
    })
    .catch(err => {
      console.error("Product load failed, falling back to default layout:", err);
      const dp = makeDefaultPages();                  // ❌ YE MISSING HAI
      setPages(dp);
      setLockedPageIds(new Set(dp.map(p => p.id)));    // ❌ YE LINE ADD KARO
      setLayoutDefs(PAGE_DEFS.filter(d => d.id !== "cover"));
    });
}, [slug]);

  const openTemplates = () => {
    setShowTemplates(true);
    setMobileLeftOpen(false);
    if (templates.length === 0 && !templatesLoading) {
      setTemplatesLoading(true);
      setTemplatesError("");
      const query = productSlug ? `?productSlug=${encodeURIComponent(productSlug)}` : "";
      apiFetch(`/photobook/list.php${query}`)
        .then(data => setTemplates(data.data || []))
        .catch(err => setTemplatesError(err.message || "Templates load nahi ho paaye."))
        .finally(() => setTemplatesLoading(false));
    }
  };

  const applyTemplate = (tpl) => {
    if (!tpl.pages || tpl.pages.length === 0) return;
    const { pages: newPages, layoutDefs: newLayoutDefs } = pagesFromTemplate(tpl.pages);
    setHistory(h => [...h.slice(-30), pages]);
    setFuture([]);
    setPages(newPages);
      setLockedPageIds(new Set(newPages.map(p => p.id)));  
    setLayoutDefs(newLayoutDefs.length ? newLayoutDefs : PAGE_DEFS.filter(d => d.id !== "cover"));
    setCur(0);
    setSelectedFrame(null);
      fillTargetRef.current = null;
    setSaveStatus("saving");
    setTimeout(() => setSaveStatus("saved"), 1200);
    setShowTemplates(false);
  };

  const updatePage = (idx, fn) => pushHistory(pages.map((p, i) => i === idx ? { ...p, ...fn(p) } : p));

   const handlePhotoClick = (fid, pageIdx = cur) => {
    if (fid === null) { setSelectedFrame(null); return; }
    setSelectedFrame(fid);
    setActivePanel("image");
    fillTargetRef.current = { pageIdx, frameId: fid };
    // 👇 Mobile pe panel sirf hamburger se khulega, frame click se auto-open nahi hoga
    if (typeof window !== "undefined" && window.innerWidth >= 768) {
      setRightOpen(true);
    }
  };
  const handleCaptionClick = (fid, pageIdx = cur) => { setCaptionPopup({ frameId: fid, pageIdx }); };
  const saveCaptionPopup = (val) => {
    if (!captionPopup) return;
    updatePage(captionPopup.pageIdx, p => ({ frames: p.frames.map(f => f.id === captionPopup.frameId ? { ...f, caption: val } : f) }));
    setCaptionPopup(null);
  };

  // 👇 Sab pages mein pehla khaali frame dhoondta hai (front cover se shuru)
  const findFirstEmptyFrame = () => {
    for (let p = 0; p < pages.length; p++) {
      const frames = pages[p].frames || [];
      const found = frames.find(f => !f.image);
      if (found) return { pageIdx: p, frameId: found.id };
    }
    return null;
  };

  const handleFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    setUploadedImages(prev => [...prev, { id: Date.now(), url, name: file.name }]);

    // 👇 Agar koi frame explicitly select nahi hai, to sabse pehla khaali frame target bano
    if (!fillTargetRef.current) {
      fillTargetRef.current = findFirstEmptyFrame();
    }
    const target = fillTargetRef.current;
    if (!target) return;

    const targetPage = pages[target.pageIdx];
    if (!targetPage) return;

    const updatedFrames = targetPage.frames.map(f => f.id === target.frameId
      ? { ...f, image: url, imgOffset: { x: 0, y: 0 }, imgZoom: 1, rotation: 0, flipH: false, filter: "none" }
      : f);

    updatePage(target.pageIdx, () => ({ frames: updatedFrames }));

    // 👇 Agla khaali frame dhoondo — pehle isi page mein, phir aage ke pages mein
    let next = null;
    const startIdx = updatedFrames.findIndex(f => f.id === target.frameId);
    for (let i = startIdx + 1; i < updatedFrames.length; i++) {
      if (!updatedFrames[i].image) { next = { pageIdx: target.pageIdx, frameId: updatedFrames[i].id }; break; }
    }
    if (!next) {
      for (let p = target.pageIdx + 1; p < pages.length; p++) {
        const frames = pages[p].frames || [];
        const found = frames.find(f => !f.image);
        if (found) { next = { pageIdx: p, frameId: found.id }; break; }
      }
    }

    fillTargetRef.current = next;
    if (next) {
      setSelectedFrame(next.frameId);
      setCur(next.pageIdx);
      setActivePage("left");
      setActivePanel("image");
    } else {
      setSelectedFrame(null);
    }
  };
  const handleMultipleFiles = (files) => Array.from(files).forEach(f => handleFile(f));
  const deleteUploadedImage = (id) => setUploadedImages(prev => prev.filter(img => img.id !== id));

  const handleAutofill = () => {
    if (!uploadedImages.length) return;
    let imgIdx = 0;
    pushHistory(pages.map(p => ({ ...p, frames: p.frames.map(f => { if (!f.image && imgIdx < uploadedImages.length) { return { ...f, image: uploadedImages[imgIdx++].url }; } return f; }) })));
  };

  const handleFrameDrop = (fid, file, pageIdx = cur, url = null) => {
    let finalUrl = url;
    if (!finalUrl && file && file.type?.startsWith("image/")) { finalUrl = URL.createObjectURL(file); setUploadedImages(prev => [...prev, { id: Date.now(), url: finalUrl, name: file.name }]); }
    if (!finalUrl) return;
    updatePage(pageIdx, p => ({ frames: p.frames.map(f => f.id === fid ? { ...f, image: finalUrl, imgOffset:{x:0,y:0}, imgZoom:1, rotation:0, flipH:false, flipV:false, filter:"none" } : f) }));
  };

  const updateFrameProp = (fid, props, pageIdx = cur) => {
    setPages(prev => prev.map((p, i) => i !== pageIdx ? p : { ...p, frames: p.frames.map(f => f.id === fid ? { ...f, ...props } : f) }));
  };

  const handleCoverTextChange = (tid, val) => {
    setPages(prev => prev.map((p, i) => i !== 0 ? p : { ...p, texts: p.texts.map(t => t.id === tid ? { ...t, content: val,isDefault: false } : t) }));
  };




  const addPage = () => {
    const defIdx = Math.min(pages.length, layoutDefs.length - 1);
    const def = layoutDefs[defIdx] || layoutDefs[0];
    const np = { id: Date.now(), isCover: false, bg: "#FFFFFF", defId: def.id,
      frames: def.photoFrames.map((pf, i) => ({ id: i, ...pf, image: null, color: ["#D8D5D0","#CFCBC6","#D0CCCA","#C8C4C0"][i%4], caption:"" })), texts: [] };
    const next = [...pages.slice(0, cur + 1), np, ...pages.slice(cur + 1)];
    pushHistory(next); setCur(cur + 1);
  };


  const addPages = (count = 3) => {
  const lastPage = pages[pages.length - 1];
  const def = layoutDefs.find(d => d.id === lastPage?.defId) || layoutDefs[0];
  const newPages = Array.from({ length: count }, (_, k) => ({
    id: Date.now() + k,
    isCover: false,
    bg: "#FFFFFF",
    defId: def.id,
    frames: def.photoFrames.map((pf, i) => ({
      id: i, ...pf, image: null,
      color: ["#D8D5D0","#CFCBC6","#D0CCCA","#C8C4C0"][i % 4],
      caption: "",
    })),
    texts: [],
  }));
  pushHistory([...pages, ...newPages]);
  setCur(pages.length);
};
const deletePage = () => {
  if (pages.length <= 1) return;
  if (lockedPageIds.has(pages[cur].id)) return; // 👈 fixed template pages delete nahi honge
  const next = pages.filter((_, i) => i !== cur);
  pushHistory(next); setCur(Math.min(cur, next.length - 1));
};

  const duplicatePage = () => {
    if (cur === 0) return;
    const p = pages[cur];
    const clone = { ...p, id: Date.now(), frames: p.frames.map(f => ({...f})), texts: [...p.texts] };
    const next = [...pages.slice(0, cur + 1), clone, ...pages.slice(cur + 1)];
    pushHistory(next);
  };

  const togglePanel = (p) => { if (activePanel === p) { setActivePanel(null); setRightOpen(false); } else { setActivePanel(p); setRightOpen(true); } };
  const captionFrame = captionPopup ? pages[captionPopup.pageIdx]?.frames.find(f => f.id === captionPopup.frameId) : null;
  const isCover = page.isCover;

  const getActiveTextPageIdx = () => {
  if (isCover) return 0;
  const LAST = pages.length - 1;
  const isSpreadPair = cur >= 2 && cur < LAST && cur % 2 === 0;
  if (isSpreadPair) return activePage === "right" ? cur + 1 : cur;
  return cur;
};  

const RightPanel = () => {
    if (activePanel === "image") {
      const activePageIdx = activePage === "right" ? cur + 1 : cur;
      const sf = selectedFrame !== null ? pages[activePageIdx]?.frames.find(f => f.id === selectedFrame) : null;
      return (
        <div className="p-4 space-y-4">
          <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Edit Image:</p>
          {sf?.image ? (
            <>
              <div>
                <div className="flex justify-between mb-1">
                  <p className="text-[9px] text-gray-400 uppercase tracking-wider">Zoom</p>
                  <span className="text-[9px] text-gray-400">{Math.round((sf.imgZoom||1)*100 - 100)}</span>
                </div>
                <input type="range" min="100" max="300" value={(sf.imgZoom||1)*100}
                  onChange={e => updateFrameProp(selectedFrame, { imgZoom: +e.target.value/100 }, activePageIdx)}
                  className="w-full cursor-pointer" style={{ accentColor:"#374151" }}/>
              </div>
              <div>
                <p className="text-[9px] text-gray-400 mb-2 uppercase tracking-wider">Filters</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {[["none","Original"],["grayscale","Grayscale"],["sepia","Sepia"],["brightness","Bright"]].map(([f,lbl]) => (
                    <button key={f} onClick={() => updateFrameProp(selectedFrame, { filter: f }, activePageIdx)}
                      className={`py-1.5 text-[10px] rounded border transition-colors ${(sf.filter||"none")===f?"bg-gray-800 border-gray-800 text-white":"border-gray-200 text-gray-600 hover:border-gray-400"}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={() => fileRef.current.click()} className="w-full py-1.5 text-[10px] bg-gray-700 text-white rounded-lg hover:bg-gray-900 transition-colors">Replace Image</button>
              <button onClick={() => { updateFrameProp(selectedFrame, { image:null, imgOffset:{x:0,y:0}, imgZoom:1, rotation:0, flipH:false, filter:"none" }, activePageIdx); setSelectedFrame(null); }}
                className="w-full py-1.5 text-[10px] text-red-400 border border-red-200 rounded-lg hover:bg-red-50 transition-colors">Remove Image</button>
            </>
          ) : (
            <p className="text-[9px] text-gray-400 leading-relaxed">Drag an image to a frame on the page, or select a frame and click Upload.</p>
          )}
        </div>
      );
    }

    if (activePanel === "text") {
      const textPageIdx = getActiveTextPageIdx();
    const activeText = selectedTextId !== null
  ? pages[textPageIdx]?.texts?.find(t => t.id === selectedTextId)
  : null;

      const currentAlign = activeText ? (activeText.align || "left") : captionAlign;
      const currentFont = activeText ? (activeText.font || "Inter") : captionFont;

      const updateActiveText = (props) => {
        if (!activeText) return;
        updatePage(textPageIdx, p => ({
          texts: p.texts.map(t => t.id === activeText.id ? { ...t, ...props } : t)
        }));
      };

      return (
        <div className="p-4 space-y-3">
          <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">TEXT:</p>
          <div>
            <p className="text-[9px] text-gray-400 mb-1.5 uppercase tracking-wider">Alignment</p>
            <div className="flex gap-1">
              {[["left","≡L"],["center","≡C"],["right","≡R"]].map(([a,lbl]) => (
                <button key={a}
                  onClick={() => { setCaptionAlign(a); updateActiveText({ align: a }); }}
                  className={`flex-1 py-1.5 text-[10px] rounded border transition-colors ${currentAlign===a?"bg-gray-800 border-gray-800 text-white":"border-gray-200 text-gray-500 hover:border-gray-400"}`}>{lbl}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[9px] text-gray-400 mb-1 uppercase tracking-wider">Font</p>
            <select
              value={currentFont}
              onChange={e => { setCaptionFont(e.target.value); updateActiveText({ font: e.target.value }); }}
              className="w-full border border-gray-200 rounded px-2 py-1.5 text-[10px] outline-none">
              {FONTS.map(f => <option key={f}>{f}</option>)}
            </select>
          </div>
          {activeText ? (
            <p className="text-[9px] text-green-600">Editing selected text on the page.</p>
          ) : (
            <p className="text-[9px] text-gray-400">Click a text box on the page to select it, then change its font/alignment here.</p>
          )}
        </div>
      );
    }

  if (activePanel === "layout") {
  const activePageIdx = activePage === "right" ? cur + 1 : cur;
  const layoutPage = pages[activePageIdx] || page;
  return (
    <div className="p-3 overflow-y-auto" style={{maxHeight:"calc(100vh - 200px)"}} data-lenis-prevent>
          <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-2">LAYOUTS:</p>
          <div className="grid grid-cols-2 gap-1.5">
            {layoutDefs.map(def => (
              <button key={def.id}
                onClick={() => { updatePage(activePageIdx, p => ({ defId: def.id, frames: def.photoFrames.map((pf, i) => ({ id: i, ...pf, image: p.frames[i]?.image || null, color: ["#D8D5D0","#CFCBC6","#D0CCCA","#C8C4C0"][i%4], caption: p.frames[i]?.caption || "" })) })); setSelectedFrame(null); }}
                className={`relative w-full aspect-[3/2] rounded border-2 overflow-hidden bg-gray-100 transition-all ${layoutPage.defId===def.id?"border-gray-800":"border-gray-200 hover:border-gray-400"}`}>
                {def.photoFrames.map((f, i) => (<div key={i} className="absolute bg-gray-300" style={{ left:`${f.x}%`, top:`${f.y*0.82}%`, width:`${f.w}%`, height:`${f.h*0.82}%` }}/>))}
                {layoutPage.defId===def.id && <div className="absolute top-0.5 right-0.5 w-3.5 h-3.5 bg-gray-800 rounded-full flex items-center justify-center"><svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" className="w-2 h-2"><polyline points="20 6 9 17 4 12"/></svg></div>}
              </button>
            ))}
          </div>
        </div>
      );
    }
     if (activePanel === "bg") {
      const activePageIdx = activePage === "right" ? cur + 1 : cur;
      const bgPage = pages[activePageIdx] || page;
      return (
        <div className="p-4">
          <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-3">BACKGROUNDS:</p>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {BG_COLORS.map(c => (<button key={c} onClick={() => updatePage(activePageIdx, () => ({ bg: c }))} className={`aspect-square rounded border-2 transition-all ${bgPage.bg===c?"border-gray-800":"border-transparent hover:border-gray-400"}`} style={{ background: c }}/>))}
          </div>
          <input type="color" value={bgPage.bg} onChange={e => updatePage(activePageIdx, () => ({ bg: e.target.value }))} className="w-8 h-8 rounded border border-gray-200 cursor-pointer p-0.5"/>
        </div>
      );
    }
    return <p className="text-[9px] text-gray-300 text-center p-4 mt-4">Select a tool</p>;
  };

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-[#EDECEA]" style={{ fontFamily:"'Inter',sans-serif" }}>

      {pdfLoading && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center" style={{ background:"rgba(0,0,0,0.78)" }}>
          <style>{`@keyframes pbspin{to{transform:rotate(360deg)}}`}</style>
          <div style={{ width:52,height:52,border:"4px solid rgba(255,255,255,0.2)",borderTopColor:"white",borderRadius:"50%",animation:"pbspin 0.8s linear infinite",marginBottom:20 }}/>
          <p style={{ color:"white",fontSize:16,fontWeight:700,fontFamily:"Inter,sans-serif" }}>Generating PDF...</p>
          <p style={{ color:"rgba(255,255,255,0.55)",fontSize:12,marginTop:6 }}>Please wait, do not close this page</p>
        </div>
      )}

      <header className="h-[60px] md:h-[72px] bg-white border-b border-gray-200 flex items-center px-3 md:px-6 gap-2 md:gap-6 z-30 flex-shrink-0">
        {showSuccessToast && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] bg-green-100 border border-green-300 rounded-xl px-6 py-3 shadow-lg text-center">
            <p className="text-green-800 font-semibold text-sm">Mini Photobook</p>
            <p className="text-green-700 text-xs">Project successfully saved.</p>
          </div>
        )}

               <div className="flex-shrink-0 flex items-center gap-2">
          <a href="/" className="text-xl md:text-3xl font-bold text-black ml-0 md:ml-16" style={{ fontFamily: "'Dancing Script', cursive" }}>Aishcraft</a>
          <button
            onClick={() => { setRightOpen(true); if (!activePanel) setActivePanel("image"); }}
            className="md:hidden p-1.5 text-gray-700 hover:text-black"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
        </div>
        <div className="hidden sm:flex flex-1 items-center justify-center gap-3">
          <span className="text-[15px] md:text-[19px] font-bold text-black">Mini Photobook</span>
        </div>
        <div className="flex-1 sm:flex-none flex items-center justify-end gap-1.5 md:gap-3 flex-shrink-0">
          <span className="hidden sm:inline text-black text-xl font-bold">•</span>
          {editingName
            ? <input autoFocus value={projectName} onChange={e => setProjectName(e.target.value)} onBlur={() => setEditingName(false)} onKeyDown={e => e.key==="Enter"&&setEditingName(false)} className="text-[13px] md:text-[17px] font-bold text-black border-b border-gray-400 outline-none bg-transparent w-24 md:w-32 text-center"/>
            : <button onClick={() => setEditingName(true)} className="hidden sm:inline text-[17px] text-gray-700 hover:text-gray-700">{projectName || "My Project"}</button>
          }

          {/* <Tip label="Save project"> */}
  {/* <button
    onClick={() => setShowSave(true)}
    className="w-7 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
  >
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4">
      <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
      <polyline points="17 21 17 13 7 13 7 21"/>
      <polyline points="7 3 7 8 15 8"/>
    </svg>
  </button>
</Tip> */}

{/* <Tip label="Preview">
<button
  onClick={() => setShowPreview(true)}
  className="flex items-center justify-center text-gray-700 hover:text-black transition-colors"
>
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-7 h-7 md:w-8 md:h-8">
    <circle cx="12" cy="12" r="10"/>
    <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none"/>
  </svg>
</button>
</Tip> */}

<button
//   onClick={async () => {
//     setPdfLoading(true);
//     try {

//       const preparedPages = await preparePagesForPdf(pages);
//       setPdfRenderPages(preparedPages);


//       await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

//       const pdfBlob = await generatePdfBlob(preparedPages, projectName, size);

//       const fd = new FormData();
//       fd.append("pdf", pdfBlob, `${projectName || "photobook"}.pdf`);
//       fd.append("productName", productData?.name || "Photobook");
//       fd.append("projectName", projectName || "My Project");
//       fd.append("pagesCount", pages.length);
//       fd.append("size", size || "");

//       const uploadRes = await apiFetch("/public/photobook-order-upload.php", {
//         method: "POST",
//         body: fd,
//       });

//       addToCart({
//         id: `photobook-${slug}-${Date.now()}`,
//         name: productData?.name || projectName || "Photobook",
//         subtitle: "PHOTOBOOK",
//         price: productData?.price || 499,
//         comparePrice: productData?.comparePrice || productData?.compare_price || null,
//         image: productData?.image || pages?.[0]?.frames?.[0]?.image || "/photobook-placeholder.png",
//         basePath: "photobook",
//         personalisedName: projectName || null,
//         paperType: null,
//         coverType: null,
//         pdfUrl: uploadRes?.data?.url || null,
//         orderRef: uploadRes?.data?.orderRef || null,
//       });

//       navigate("/cartPage");
//     } catch (err) {
//       console.error("PDF generation/upload failed:", err);
//       alert("PDF generate karte waqt error aa gaya. Dobara try karein.");
//     } finally {
//       setPdfRenderPages(null);
//       setPdfLoading(false);
//     }
//   }}
//   className="flex items-center gap-1 md:gap-2 bg-black text-white text-[11px] md:text-[15px] font-medium px-2 md:px-5 py-2 md:py-2.5 rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap"
// >
onClick={async () => {                                    
  setShowSuccessToast(true);                               
  await new Promise(r => setTimeout(r, 2000));              
  setShowSuccessToast(false);                             

  const itemId = `photobook-${slug}-${Date.now()}`;    

    addToCart({
      id: itemId,
      name: productData?.name || projectName || "Photobook",
      subtitle: "PHOTOBOOK",
      price: productData?.price || 499,
      comparePrice: productData?.comparePrice || productData?.compare_price || null,
      image: productData?.image || pages?.[0]?.frames?.[0]?.image || "/photobook-placeholder.png",
      basePath: "photobook",
      personalisedName: projectName || null,
      paperType: null,
      coverType: null,
      pdfUrl: null,
      orderRef: null,
    });

    navigate("/cartPage");

    (async () => {
      let hiddenDiv = null;
      let root = null;
      try {
       const preparedPages = stripDefaultTexts(await preparePagesForPdf(pages));

        hiddenDiv = document.createElement("div");
        hiddenDiv.style.position = "fixed";
        hiddenDiv.style.top = "0";
        hiddenDiv.style.left = "-99999px";
        hiddenDiv.style.overflow = "visible";
        hiddenDiv.setAttribute("aria-hidden", "true");
        document.body.appendChild(hiddenDiv);

        root = createRoot(hiddenDiv);
        root.render(
          <>
            {preparedPages.map((pg) => (
              <div
                key={`pdf-${pg.id}`}
                style={{
                  width: pg.isCover ? 1600 : 800,
                  height: 1131,
                  background: pg.bg,
                }}
              >
                {renderPageForPdf(pg)}
              </div>
            ))}
          </>
        );

        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

        const pdfBlob = await generatePdfBlob(preparedPages, projectName, size, hiddenDiv);

        const fd = new FormData();
        fd.append("pdf", pdfBlob, `${projectName || "photobook"}.pdf`);
        fd.append("productName", productData?.name || "Photobook");
        fd.append("projectName", projectName || "My Project");
        fd.append("pagesCount", pages.length);
        fd.append("size", size || "");

        const uploadRes = await apiFetch("/public/photobook-order-upload.php", {
          method: "POST",
          body: fd,
        });

        // 👇 NAYA: PDF ban jaane ke baad, cart item ko orderRef + pdfUrl se update karo
        // taaki checkout page ko pata rahe kis order pe customer info attach karni hai
        updateCartItem(itemId, {
          pdfUrl: uploadRes?.data?.url || null,
          orderRef: uploadRes?.data?.orderRef || null,
        });

        console.log("PDF background mein successfully generate aur upload ho gayi.");
      } catch (err) {
        console.error("Background PDF generation/upload failed:", err);
      } finally {
        if (root) root.unmount();
        if (hiddenDiv && hiddenDiv.parentNode) hiddenDiv.parentNode.removeChild(hiddenDiv);
      }
    })();
  }}
  className="flex items-center gap-1 md:gap-2 bg-black text-white text-[11px] md:text-[15px] font-medium px-2 md:px-5 py-2 md:py-2.5 rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap"
>
  <svg width="14" height="14" className="md:w-5 md:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
  </svg>
  <span className="hidden sm:inline"> Add to cart</span>
  <span className="inline sm:hidden">Save & Cart</span>
  <svg width="12" height="12" className="hidden sm:block md:w-4 md:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 18l6-6-6-6"/>
  </svg>
</button>
          <button onClick={() => window.location.href = '/'} className="text-gray-500 hover:text-red-600 p-1 transition-colors">
            <svg width="20" height="20" className="md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </header>

      {/* <div className="md:hidden flex items-center justify-around bg-white border-b border-gray-200 py-2 flex-shrink-0 z-20">
        <button onClick={() => setMobileLeftOpen(true)} className="flex flex-col items-center gap-0.5 text-gray-600 px-3">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-5 h-5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
          <span className="text-[10px] font-medium">Photos</span>
        </button>
        <button onClick={() => togglePanel(activePanel || "image")} className="flex flex-col items-center gap-0.5 text-gray-600 px-3">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-5 h-5"><path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zM12 2v20M2 12h20"/></svg>
          <span className="text-[10px] font-medium">Tools</span>
        </button>
        <button onClick={() => setShowOrganize(true)} className="flex flex-col items-center gap-0.5 text-gray-600 px-3">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-5 h-5"><rect x="2" y="3" width="6" height="18" rx="1"/><rect x="9" y="3" width="6" height="11" rx="1"/><rect x="16" y="3" width="6" height="7" rx="1"/></svg>
          <span className="text-[10px] font-medium">Pages</span>
        </button>
      </div> */}

      <div className="flex flex-1 min-h-0 relative">

        {(mobileLeftOpen || rightOpen) && (
          <div
            className="md:hidden fixed inset-0 bg-black/30 z-30"
            onClick={() => { setMobileLeftOpen(false); setRightOpen(false); setActivePanel(null); }}
          />
        )}

        <div
          className={`bg-white border-r border-gray-200 flex flex-col flex-shrink-0 transition-transform duration-300
            fixed inset-y-0 left-0 z-40 w-[82vw] max-w-[300px]
            ${mobileLeftOpen ? "translate-x-0" : "-translate-x-full"}
            md:static md:translate-x-0 md:w-[270px] md:z-auto`}
        >
          <div className="md:hidden flex items-center justify-between px-3 py-3 border-b border-gray-100">
            <span className="text-sm font-bold text-gray-700">Photos</span>
            <button onClick={() => setMobileLeftOpen(false)} className="p-1 text-gray-500">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

        

          <div className="px-3 pt-3 pb-2 border-b border-gray-100 mr-[14px]">
            <p className="flex-1 text-center text-lg font-semibold text-gray-700">Upload</p>
            {uploadedImages.length > 0 && (
              <div className="flex items-center gap-1">
                <button onClick={handleAutofill} className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-800 border border-gray-200 rounded px-1.5 py-0.5 transition-colors">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3 h-3"><path d="M5 3l14 9-14 9V3z"/></svg>
                  Autofill
                </button>
                {[["lg","M3 3h18v18H3z"],["md","M3 3h8v8H3zM13 3h8v8h-8zM3 13h8v8H3zM13 13h8v8h-8z"],["sm","M3 3h4v4H3zM10 3h4v4h-4zM17 3h4v4h-4zM3 10h4v4H3zM10 10h4v4h-4zM17 10h4v4h-4zM3 17h4v4H3zM10 17h4v4h-4zM17 17h4v4h-4z"]].map(([sz,p]) => (
                  <button key={sz} onClick={() => setGallerySize(sz)} className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${gallerySize===sz?"bg-gray-800 text-white":"text-gray-400 hover:bg-gray-100"}`}>
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3"><path d={p}/></svg>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div  className="flex-1 overflow-y-auto p-2 flex flex-col"
  data-lenis-prevent onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); handleMultipleFiles(e.dataTransfer.files); }}>
            {uploadedImages.length === 0 ? (
              <div className="h-full flex flex-col p-2">
                <div className="flex-1 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-4 cursor-pointer hover:border-gray-400 transition-colors px-5 py-8" onClick={() => fileRef.current.click()}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.3" className="w-10 h-10"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/></svg>
                  <p className="text-[11px] text-gray-500 text-center leading-relaxed">Drag and drop images or<br/>upload from your computer.</p>
                  <button onClick={e => { e.stopPropagation(); fileRef.current.click(); }} className="w-full py-2.5 bg-gray-700 text-white text-xs font-semibold rounded-lg hover:bg-gray-900 transition-colors flex items-center justify-center gap-1.5">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3.5 h-3.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Upload images
                  </button>
                </div>
              </div>
            ) : (
              <div className={`grid gap-1.5 ${gallerySize==="lg"?"grid-cols-1":gallerySize==="md"?"grid-cols-2":"grid-cols-3"}`}>
                {uploadedImages.map(img => (
                  <div key={img.id} className="relative group cursor-grab rounded-lg overflow-hidden bg-gray-100" style={{ aspectRatio:"1" }} draggable
                    onDragStart={e => { e.dataTransfer.setData("imageUrl", img.url); e.dataTransfer.effectAllowed = "copy"; }}>
                    <img src={img.url} alt="" className="w-full h-full object-cover"/>
                    <button onClick={() => deleteUploadedImage(img.id)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-2.5 h-2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[7px] text-center py-0.5 opacity-0 group-hover:opacity-100 transition-opacity truncate px-1">{img.name?.slice(0,20)}</div>
                  </div>
                ))}
                <div onClick={() => fileRef.current.click()} className="rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 flex items-center justify-center cursor-pointer transition-colors bg-gray-50" style={{ aspectRatio:"1" }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" className="w-5 h-5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </div>
              </div>
            )}
          </div>
          {uploadedImages.length > 0 && (
            <div className="px-2 pb-2 pt-1 border-t border-gray-100">
              <button onClick={() => fileRef.current.click()} className="w-full py-1.5 bg-gray-700 text-white text-xs font-semibold rounded-lg hover:bg-gray-900 transition-colors flex items-center justify-center gap-1.5">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3 h-3"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Upload images
              </button>
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={e => handleMultipleFiles(e.target.files)}/>
        </div>

        <div className="flex-1 flex flex-col min-h-0 relative overflow-hidden">
          {selectedFrame !== null && (() => {
            const activePageIdx = activePage === "right" ? cur + 1 : cur;
            const sf = pages[activePageIdx]?.frames.find(f => f.id === selectedFrame);
            if (!sf?.image) return null;
            return (
             <div data-selection-pill className="absolute top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1 bg-white rounded-full shadow-lg border border-gray-200 px-3 py-1.5">
                <Tip label="Rotate left"><button onClick={() => updateFrameProp(selectedFrame, { rotation: ((sf.rotation||0) - 90 + 360) % 360 }, activePageIdx)} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-600 transition-colors"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M3 7h10a5 5 0 010 10H9"/><path d="M3 7l4-4M3 7l4 4"/></svg></button></Tip>
                <Tip label="Rotate right"><button onClick={() => updateFrameProp(selectedFrame, { rotation: ((sf.rotation||0) + 90) % 360 }, activePageIdx)} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-600 transition-colors"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M21 7H11a5 5 0 000 10h4"/><path d="M21 7l-4-4M21 7l-4 4"/></svg></button></Tip>
                <Tip label="Flip horizontal"><button onClick={() => updateFrameProp(selectedFrame, { flipH: !sf.flipH }, activePageIdx)} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-600 transition-colors"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M8 3H5a2 2 0 00-2 2v14a2 2 0 002 2h3"/><path d="M16 3h3a2 2 0 012 2v14a2 2 0 01-2 2h-3"/><line x1="12" y1="3" x2="12" y2="21" strokeDasharray="3 2"/></svg></button></Tip>
                <Tip label="Remove image"><button onClick={() => { updateFrameProp(selectedFrame, { image:null, imgOffset:{x:0,y:0}, imgZoom:1, rotation:0, flipH:false, filter:"none" }, activePageIdx); setSelectedFrame(null); }} className="p-1.5 rounded-full hover:bg-red-50 text-red-400 transition-colors"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg></button></Tip>
              </div>
            );
          })()}

          <div className="hidden md:flex absolute top-3 right-4 z-10 items-center gap-1.5">
            <button onClick={() => setZoom(z => Math.max(30, z-10))} className="text-gray-400 hover:text-gray-600 font-bold text-base w-5 text-center leading-none select-none">−</button>
            <div className="relative flex items-center" style={{width:100}}>
              <div className="absolute w-full h-0.5 bg-gray-300 rounded"/>
              <input type="range" min="30" max="130" value={zoom} onChange={e => setZoom(+e.target.value)} className="relative w-full cursor-pointer appearance-none bg-transparent z-10" style={{ height:14, accentColor:"#374151" }}/>
            </div>
            <button onClick={() => setZoom(z => Math.min(130, z+10))} className="text-gray-400 hover:text-gray-600 font-bold text-base w-5 text-center leading-none select-none">+</button>
          </div>

         <div className="hidden md:flex flex-1 items-center justify-center relative" style={{ overflow:"hidden" }} onClick={e => { if (e.target === e.currentTarget) setSelectedFrame(null); }}>  
            {cur > 0 && (
              <button onClick={() => setCur(c => { if (c===1) return 0; if (c<=2) return 1; const ps=c%2===0?c-2:c-1; return ps; })} className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 bg-white rounded-full shadow border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-800 z-20 transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
            )}

            <div style={{ transform:`scale(${zoom/100})`, transformOrigin:"center center", transition:"transform 0.15s", display:"flex", alignItems:"center", justifyContent:"center" }}>
              {(() => {
                const LAST = pages.length - 1;
                const isSpreadPair = cur >= 2 && cur < LAST && cur % 2 === 0;
                const leftPage = isSpreadPair ? pages[cur] : null;
                const rightPage = isSpreadPair ? pages[cur + 1] : null;

  if (isCover) return (
  <div style={{ width:960 }}>
    <div style={{ height:400, boxShadow:"0 8px 40px rgba(0,0,0,0.15)", borderRadius:2, overflow:"hidden" }}>
      <CoverSpread page={page} selectedFrame={selectedFrame} editingCoverText={editingCoverText}
        onPhotoClick={handlePhotoClick} onCaptionClick={handleCaptionClick} onFrameDrop={handleFrameDrop}
        onFrameUpdate={(fid, props) => updateFrameProp(fid, props, 0)}  
       onCoverTextDblClick={(tid) => { setSelectedTextId(tid); setEditingCoverText(tid); }}
        onCoverTextChange={handleCoverTextChange}
        onCoverTextBlur={() => setEditingCoverText(null)}/>
    </div>
    <div className="flex justify-between text-xs mt-2">
      <span className="text-gray-500">Back Cover</span>
      <span className="text-gray-700 font-medium">Front Cover</span>
    </div>
  </div>
);

                if (isSpreadPair) return (
                  <div className="flex" style={{ gap:6 }}>
                    <div className="relative" style={{ width:470, height:400 }} onClick={() => setActivePage("left")}>
                      <div className="absolute rounded-sm overflow-hidden" style={{ inset:0, background: leftPage.bg, boxShadow:"0 4px 20px rgba(0,0,0,0.12)" }}>
                        {leftPage.isCollage ? (
                          <CollageCanvas page={leftPage} selectedFrame={activePage==="left"?selectedFrame:null}
                            editingText={activePage==="left" ? editingCoverText : null}
                            onPhotoClick={(fid) => { setActivePage("left"); handlePhotoClick(fid, cur); }}
                            onFrameDrop={(fid,file,url) => handleFrameDrop(fid, file, cur, url)}
                            onFrameUpdate={(fid,props) => updateFrameProp(fid, props, cur)}
                         onTextDblClick={(tid) => { setActivePage("left"); setSelectedTextId(tid); setEditingCoverText(tid); }}
                            onTextChange={(tid,val) => setPages(prev => prev.map((p,i) => i!==cur ? p : { ...p, texts: p.texts.map(t => t.id===tid ? { ...t, content: val,isDefault: false } : t) }))}
                            onTextBlur={() => setEditingCoverText(null)}/>
                        ) : leftPage.isPolaroid ? (
                          <PolaroidPageCanvas page={leftPage} selectedFrame={activePage==="left"?selectedFrame:null}
                            editingText={activePage==="left" && editingCoverText === leftPage.texts[0]?.id}
                            onPhotoClick={(fid) => { setActivePage("left"); handlePhotoClick(fid, cur); }}
                            onFrameDrop={(fid,file,url) => handleFrameDrop(fid, file, cur, url)}
                            onFrameUpdate={(fid,props) => updateFrameProp(fid, props, cur)}
                          onTextDblClick={() => { setActivePage("left"); setSelectedTextId(leftPage.texts[0].id); setEditingCoverText(leftPage.texts[0].id); }}
                            onTextChange={(val) => setPages(prev => prev.map((p,i) => i!==cur ? p : { ...p, texts:[{ ...p.texts[0], content: val,isDefault: false }] }))}
                            onTextBlur={() => setEditingCoverText(null)}/>
                        ) : renderPolaroidPage(leftPage, {
                            selectedFrame: activePage==="left" ? selectedFrame : null,
                            editingCoverText: activePage==="left" ? editingCoverText : null,
                            onPhotoClick: (fid) => { setActivePage("left"); handlePhotoClick(fid, cur); },
                            onFrameDrop: (fid,file,url) => handleFrameDrop(fid, file, cur, url),
                            onFrameUpdate: (fid,props) => updateFrameProp(fid, props, cur),
                           onTextDblClickMulti: (tid) => { setActivePage("left"); setSelectedTextId(tid); setEditingCoverText(tid); },
                            onTextChangeMulti: (tid,val) => setPages(prev => prev.map((p,i) => i!==cur ? p : { ...p, texts: p.texts.map(t => t.id===tid ? { ...t, content: val,isDefault: false } : t) })),
                            onTextBlur: () => setEditingCoverText(null),
                          }) || (
                       <PageCanvas page={leftPage} selectedFrame={activePage==="left"?selectedFrame:null}
  onPhotoClick={(fid) => { setActivePage("left"); handlePhotoClick(fid, cur); }}
  onCaptionClick={(fid) => { setActivePage("left"); handleCaptionClick(fid, cur); }}
  onFrameDrop={(fid,file,_,url) => handleFrameDrop(fid, file, cur, url)}
  onFrameUpdate={(fid,props) => updateFrameProp(fid, props, cur)}
  editingText={activePage==="left" ? editingCoverText : null}
  onTextDblClick={(tid) => { setActivePage("left"); setSelectedTextId(tid); setEditingCoverText(tid); }}
  onTextChange={(tid,val) => setPages(prev => prev.map((p,i) => i!==cur ? p : { ...p, texts: p.texts.map(t => t.id===tid ? { ...t, content: val,isDefault: false } : t) }))}
  onTextBlur={() => setEditingCoverText(null)}/>
                        )}
                      </div>
                      <div className="absolute pointer-events-none" style={{ inset:-1, border:"1px solid #e879f9" }}>
                        <span className="absolute -top-3 left-1 text-[8px] text-pink-400 bg-[#EDECEA] px-0.5 leading-none">safe area</span>
                      </div>
                    </div>
                    <div className="relative" style={{ width:470, height:400 }} onClick={() => setActivePage("right")}>
                      <div className="absolute rounded-sm overflow-hidden" style={{ inset:0, background: rightPage.bg, boxShadow:"0 4px 20px rgba(0,0,0,0.12)" }}>
                        {rightPage.isCollage ? (
                          <CollageCanvas page={rightPage} selectedFrame={activePage==="right"?selectedFrame:null}
                            editingText={activePage==="right" ? editingCoverText : null}
                            onPhotoClick={(fid) => { setActivePage("right"); handlePhotoClick(fid, cur+1); }}
                            onFrameDrop={(fid,file,url) => handleFrameDrop(fid, file, cur+1, url)}
                            onFrameUpdate={(fid,props) => updateFrameProp(fid, props, cur+1)}
                          onTextDblClick={(tid) => { setActivePage("right"); setSelectedTextId(tid); setEditingCoverText(tid); }}
                            onTextChange={(tid,val) => setPages(prev => prev.map((p,i) => i!==cur+1 ? p : { ...p, texts: p.texts.map(t => t.id===tid ? { ...t, content: val,isDefault: false } : t) }))}
                            onTextBlur={() => setEditingCoverText(null)}/>
                        ) : rightPage.isPolaroid ? (
                          <PolaroidPageCanvas page={rightPage} selectedFrame={activePage==="right"?selectedFrame:null}
                            editingText={activePage==="right" && editingCoverText === rightPage.texts[0]?.id}
                            onPhotoClick={(fid) => { setActivePage("right"); handlePhotoClick(fid, cur+1); }}
                            onFrameDrop={(fid,file,url) => handleFrameDrop(fid, file, cur+1, url)}
                            onFrameUpdate={(fid,props) => updateFrameProp(fid, props, cur+1)}
                         onTextDblClick={() => { setActivePage("right"); setSelectedTextId(rightPage.texts[0].id); setEditingCoverText(rightPage.texts[0].id); }}
                            onTextChange={(val) => setPages(prev => prev.map((p,i) => i!==cur+1 ? p : { ...p, texts:[{ ...p.texts[0], content: val,isDefault: false }] }))}
                            onTextBlur={() => setEditingCoverText(null)}/>
                        ) : renderPolaroidPage(rightPage, {
                            selectedFrame: activePage==="right" ? selectedFrame : null,
                            editingCoverText: activePage==="right" ? editingCoverText : null,
                            onPhotoClick: (fid) => { setActivePage("right"); handlePhotoClick(fid, cur+1); },
                            onFrameDrop: (fid,file,url) => handleFrameDrop(fid, file, cur+1, url),
                            onFrameUpdate: (fid,props) => updateFrameProp(fid, props, cur+1),
                           onTextDblClickMulti: (tid) => { setActivePage("right"); setSelectedTextId(tid); setEditingCoverText(tid); },
                            onTextChangeMulti: (tid,val) => setPages(prev => prev.map((p,i) => i!==cur+1 ? p : { ...p, texts: p.texts.map(t => t.id===tid ? { ...t, content: val,isDefault: false } : t) })),
                            onTextBlur: () => setEditingCoverText(null),
                          }) || (
                         <PageCanvas page={rightPage} selectedFrame={activePage==="right"?selectedFrame:null}
  onPhotoClick={(fid) => { setActivePage("right"); handlePhotoClick(fid, cur+1); }}
  onCaptionClick={(fid) => { setActivePage("right"); handleCaptionClick(fid, cur+1); }}
  onFrameDrop={(fid,file,_,url) => handleFrameDrop(fid, file, cur+1, url)}
  onFrameUpdate={(fid,props) => updateFrameProp(fid, props, cur+1)}
  editingText={activePage==="right" ? editingCoverText : null}
onTextDblClick={(tid) => { setActivePage("right"); setSelectedTextId(tid); setEditingCoverText(tid); }}
  onTextChange={(tid,val) => setPages(prev => prev.map((p,i) => i!==cur+1 ? p : { ...p, texts: p.texts.map(t => t.id===tid ? { ...t, content: val,isDefault: false } : t) }))}
  onTextBlur={() => setEditingCoverText(null)}/>
                        )}
                      </div>
                      <div className="absolute pointer-events-none" style={{ inset:-1, border:"1px solid #e879f9" }}>
                        <span className="absolute -top-3 left-1 text-[8px] text-pink-400 bg-[#EDECEA] px-0.5 leading-none">safe area</span>
                      </div>
                    </div>
                  </div>
                );

                return (
                  <div className="relative" style={{ width:460, height:400 }}>
                    <div className="absolute rounded-sm overflow-hidden" style={{ inset:0, boxShadow:"0 8px 40px rgba(0,0,0,0.15)", background: page.bg }}>
                      {page.isCollage ? (
                        <CollageCanvas page={page} selectedFrame={selectedFrame}
                          editingText={editingCoverText}
                          onPhotoClick={handlePhotoClick}
                          onFrameDrop={(fid,file,url) => handleFrameDrop(fid, file, cur, url)}
                          onFrameUpdate={(fid,props) => updateFrameProp(fid, props, cur)}
                         onTextDblClick={(tid) => { setSelectedTextId(tid); setEditingCoverText(tid); }}
                          onTextChange={(tid,val) => setPages(prev => prev.map((p,i) => i!==cur ? p : { ...p, texts: p.texts.map(t => t.id===tid ? { ...t, content: val,isDefault: false } : t) }))}
                          onTextBlur={() => setEditingCoverText(null)}/>
                      ) : page.isPolaroid ? (
                        <PolaroidPageCanvas page={page} selectedFrame={selectedFrame}
                          editingText={editingCoverText === page.texts[0]?.id}
                          onPhotoClick={handlePhotoClick}
                          onFrameDrop={(fid,file,url) => handleFrameDrop(fid, file, cur, url)}
                          onFrameUpdate={(fid,props) => updateFrameProp(fid, props, cur)}
                         onTextDblClick={() => { setSelectedTextId(page.texts[0].id); setEditingCoverText(page.texts[0].id); }}
                          onTextChange={(val) => setPages(prev => prev.map((p,i) => i!==cur ? p : { ...p, texts:[{ ...p.texts[0], content: val,isDefault: false }] }))}
                          onTextBlur={() => setEditingCoverText(null)}/>
                      ) : renderPolaroidPage(page, {
                          selectedFrame,
                          editingCoverText,
                          onPhotoClick: handlePhotoClick,
                          onFrameDrop: (fid,file,url) => handleFrameDrop(fid, file, cur, url),
                          onFrameUpdate: (fid,props) => updateFrameProp(fid, props, cur),
                         onTextDblClickMulti: (tid) => { setSelectedTextId(tid); setEditingCoverText(tid); },
                          onTextChangeMulti: (tid,val) => setPages(prev => prev.map((p,i) => i!==cur ? p : { ...p, texts: p.texts.map(t => t.id===tid ? { ...t, content: val,isDefault: false } : t) })),
                          onTextBlur: () => setEditingCoverText(null),
                        }) || (
                        <PageCanvas page={page} selectedFrame={selectedFrame}
                          onPhotoClick={handlePhotoClick} onCaptionClick={handleCaptionClick}
                          onFrameDrop={(fid,file,_,url) => handleFrameDrop(fid, file, cur, url)}
                          onFrameUpdate={(fid,props) => updateFrameProp(fid, props, cur)}
                          editingText={editingCoverText}
                       onTextDblClick={(tid) => { setSelectedTextId(tid); setEditingCoverText(tid); }}
                          onTextChange={(tid,val) => setPages(prev => prev.map((p,i) => i!==cur ? p : { ...p, texts: p.texts.map(t => t.id===tid ? { ...t, content: val,isDefault: false } : t) }))}
                          onTextBlur={() => setEditingCoverText(null)}/>
                      )}
                    </div>
                    <div className="absolute pointer-events-none" style={{ inset:-1, border:"1px solid #e879f9" }}>
                      <span className="absolute -top-3 left-1 text-[8px] text-pink-400 bg-[#EDECEA] px-0.5 leading-none">safe area</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {cur < pages.length-1 && (
              <button onClick={() => setCur(c => { const LAST=pages.length-1; if(c===0)return 1; if(c===1)return 2; const np=c%2===0?c+2:c+1; return Math.min(np,LAST); })} className="absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 bg-white rounded-full shadow border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-800 z-20 transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            )}
            
          </div>
         <MobileEditorView
  pages={pages}
  cur={cur}
  selectedFrame={selectedFrame}
  editingCoverText={editingCoverText}
  selectedTextId={selectedTextId}
  productData={productData}
  layoutDefs={layoutDefs}
  setCur={setCur}
    addPages={addPages}
  setSelectedFrame={setSelectedFrame}
  setSelectedTextId={setSelectedTextId}
  setEditingCoverText={setEditingCoverText}
  setPages={setPages}
  handlePhotoClick={handlePhotoClick}
  handleCaptionClick={handleCaptionClick}
  handleFrameDrop={handleFrameDrop}
  updateFrameProp={updateFrameProp}
  handleCoverTextChange={handleCoverTextChange}
  updatePage={updatePage}
  PageCanvas={PageCanvas}
  CoverSpread={CoverSpread}
  CollageCanvas={CollageCanvas}
  renderPolaroidPage={renderPolaroidPage}
  fileRef={fileRef}
/>
<div className="hidden md:flex py-1.5 flex-shrink-0 justify-center">
  {(() => {
    const LAST = pages.length - 1;
    const isSpreadPair = cur >= 2 && cur < LAST && cur % 2 === 0;
    if (isCover) return null;
    if (isSpreadPair) return <span className="text-xs text-gray-500"><span className="mr-24 text-gray-600 font-medium">Page {cur}</span><span className="font-medium text-gray-600">Page {cur+1}</span></span>;
    return <span className="text-xs font-medium text-gray-600">Page {cur}</span>;
  })()}
</div>
<div className="hidden md:block bg-white border-t border-gray-200 flex-shrink-0 relative">
  {/* 👇 Floating "Organize pages" pill — sirf apni content jitni width leta hai */}
  <div className="absolute -top-8 right-4 z-20">
    <button
      onClick={() => setShowOrganize(true)}
      className="flex items-center gap-1.5 text-[12px] font-medium text-gray-700 bg-white border border-gray-200 rounded-full px-3.5 py-1.5 shadow-md hover:bg-gray-50 transition-colors"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3.5 h-3.5">
        <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v13a1 1 0 01-1.6.8L8 18l-2.4 1.8A1 1 0 014 19V6z"/>
        <path d="M22 6a2 2 0 00-2-2h-6a2 2 0 00-2 2v13a1 1 0 001.6.8L16 18l2.4 1.8A1 1 0 0020 19V6z"/>
      </svg>
      Organize pages
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-2.5 h-2.5">
        <polyline points="18 15 12 9 6 15"/>
      </svg>
    </button>
  </div>

  <div className="flex items-end px-2 py-2">
            <div className="flex-1 overflow-x-auto flex items-center pb-0.5 gap-2" data-lenis-prevent style={{ scrollbarWidth:"thin", scrollbarColor:"#d1d5db transparent" }}>
                {(() => {
                  const LAST = pages.length - 1;
                  const items = [];
                  let i = 0;
                  const singleThumb = (idx, label, pg, active) => (
                    <div key={`t-${idx}`} onClick={() => setCur(idx)} className="flex-shrink-0 flex flex-col items-center cursor-pointer">
                      <div className="border-2 overflow-hidden relative transition-all" style={{ width:68, height:88, background:pg.bg, borderColor:active?"#111":"#ddd", borderRadius:3 }}>
                        {pg.frames.map((f, fi) => (<div key={fi} className="absolute" style={{ left:`${f.x}%`, top:`${f.y}%`, width:`${f.w}%`, height:`${f.h}%`, background:f.image?`url(${f.image}) center/cover`:f.color, borderRadius:1 }}/>))}
                      </div>
                      <span className={`text-[9px] mt-1 ${active?"text-gray-900 font-semibold":"text-gray-400"}`}>{label}</span>
                    </div>
                  );
                 const spreadThumb = (idx, pgL, pgR, active) => (
  <div key={`t-${idx}`} onClick={() => setCur(idx)} className="flex-shrink-0 flex flex-col items-center cursor-pointer">
    <div className="flex border-2 overflow-hidden transition-all" style={{ width:136, height:88, borderColor:active?"#111":"#ddd", borderRadius:3 }}>
      <div className="flex-1 relative overflow-hidden" style={{ background: pgL.bg }}>
        {pgL.frames.map((f, fi) => (<div key={fi} className="absolute" style={{ left:`${f.x}%`, top:`${f.y}%`, width:`${f.w}%`, height:`${f.h}%`, background:f.image?`url(${f.image}) center/cover`:f.color, borderRadius:1 }}/>))}
      </div>
      <div style={{ width:1, background:"#e5e5e5", flexShrink:0 }}/>
      <div className="flex-1 relative overflow-hidden" style={{ background:pgR.bg }}>
        {pgR.frames.map((f, fi) => (<div key={fi} className="absolute" style={{ left:`${f.x}%`, top:`${f.y}%`, width:`${f.w}%`, height:`${f.h}%`, background:f.image?`url(${f.image}) center/cover`:f.color, borderRadius:1 }}/>))}
      </div>
    </div>
    <div className="flex mt-1" style={{ gap:24 }}>
      <span className={`text-[9px] ${active?"text-gray-900 font-semibold":"text-gray-400"}`}>Page {idx}</span>
      <span className={`text-[9px] ${active?"text-gray-900 font-semibold":"text-gray-400"}`}>Page {idx+1}</span>
    </div>
  </div>
);
                  while (i < pages.length) {
                    const p = pages[i];
                    if (i === 0) {
                      const active = cur === 0;
                      items.push(
                       <div key="t-cover" onClick={() => setCur(0)} className="flex-shrink-0 flex flex-col items-center cursor-pointer">
  <div className="flex border-2 overflow-hidden transition-all" style={{ width:136, height:88, borderColor:active?"#111":"#ddd", borderRadius:3 }}>
    <div className="flex-1 relative overflow-hidden" style={{ background: p.backBg || "#FFFDF7" }}>
      {p.backBgImage && <img src={p.backBgImage} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute bottom-1 left-1.5 text-[4px] text-gray-300 italic" style={{fontFamily:"serif", zIndex:2}}>Aishcraft</div>
    </div>
    <div style={{ width:1, background:"#ccc", flexShrink:0 }}/>
    <div className="flex-1 relative overflow-hidden" style={{ background: p.bg }}>
      {p.bgImage && <img src={p.bgImage} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      {p.frames.map((f, fi) => (<div key={fi} className="absolute" style={{ left:`${f.x}%`, top:`${f.y}%`, width:`${f.w}%`, height:`${f.h}%`, background:f.image?`url(${f.image}) center/cover`:f.color }}/>))}
    </div>
  </div>
  <div className="flex mt-1" style={{ gap:12 }}>
    <span className="text-[9px] text-gray-400">Back Cover</span>
    <span className={`text-[9px] ${active?"text-gray-900 font-semibold":"text-gray-400"}`}>Front Cover</span>
  </div>
</div>
                      );
                      i++;
                    } else if (i === 1) { items.push(singleThumb(1, "Page 1", p, cur===1)); i++; }
                    else if (i === LAST) { items.push(singleThumb(LAST, `Page ${LAST}`, p, cur===LAST)); i++; }
                    else if (i % 2 === 0 && i + 1 < pages.length) { const p2=pages[i+1]; const active=cur===i||cur===i+1; items.push(spreadThumb(i, p, p2, active)); i += 2; }
                    else { items.push(singleThumb(i, `Page ${i}`, p, cur===i)); i++; }
                  }
                  return items;
                })()}
              </div>
              <div className="flex-shrink-0 flex flex-col gap-1 ml-2 pl-2 border-l border-gray-200 mb-12">
                <Tip label="Add page" side="left">
                  <button onClick={addPage} className="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300 flex items-center justify-center text-gray-600 mb-1">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3 h-3"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  </button>
                </Tip>
            <Tip label={lockedPageIds.has(pages[cur]?.id) ? "Cannot remove this page" : "Delete page"} side="left">
  <button
    onClick={deletePage}
    className={`w-6 h-6 rounded-full border flex items-center justify-center mb-1 transition-colors
      ${lockedPageIds.has(pages[cur]?.id)
        ? "bg-gray-100 border-gray-300 text-gray-300 cursor-not-allowed"
        : "bg-gray-100 hover:bg-red-100 border-gray-300 hover:border-red-300 text-gray-500 hover:text-red-500"}`}
  >
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3 h-3">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
    </svg>
  </button>
</Tip>
              </div>
            </div>
          </div>
        </div>

   <div className="flex flex-shrink-0">
         <div
  data-mobile-tool-panel
  className={`block overflow-hidden transition-all duration-300 bg-white border-l border-gray-100
      fixed inset-y-0 right-0 z-40 w-[78vw] max-w-[280px]
      ${rightOpen ? "translate-x-0" : "translate-x-full"}
      md:static md:translate-x-0 md:z-auto md:max-w-none ${rightOpen ? "md:w-44" : "md:w-0"}`}
  > 
            <div className="md:hidden flex items-center justify-between px-3 py-3 border-b border-gray-100">
              <span className="text-sm font-bold text-gray-700">Tools</span>
              <button onClick={() => { setRightOpen(false); setActivePanel(null); }} className="p-1 text-gray-500">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          <div className="md:hidden flex flex-col gap-1.5 px-3 py-3 border-b border-gray-100">
  {[
    { key:"image", label:"Image" }, { key:"text", label:"Text" },
    { key:"layout", label:"Layouts" }, { key:"bg", label:"BG" },
  ].map(({key,label}) => (
    <button key={key} onClick={() => setActivePanel(key)}
      className={`w-full text-left px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${activePanel===key?"bg-gray-900 text-white":"bg-gray-100 text-gray-600"}`}>
      {label}
    </button>
  ))}
</div>
          {rightOpen && <div className="w-full md:w-44 h-full overflow-y-auto" data-lenis-prevent><RightPanel/></div>}
          </div>
          <div className="hidden md:flex w-12 bg-white border-l border-gray-200 flex-col items-center py-3 gap-1">
            {[
              { key:"image", label:"Image", path:"M3 3h18v18H3zM8.5 8.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM21 15l-5-5L5 21" },
              { key:"text",  label:"Text",  path:"M4 7V4h16v3M9 20h6M12 4v16" },
              { key:"layout",label:"Layouts",path:"M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" },
              { key:"bg",    label:"BG",    path:"M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zM12 2v20M2 12h20" },
              { key:"text_opts", label:"Text Options", path:"M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" },
            ].map(({ key, label, path }) => (
              <Tip key={key} label={label} side="bottom">
                <button onClick={() => togglePanel(key === "text_opts" ? "text" : key)}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${activePanel===(key==="text_opts"?"text":key)?"bg-gray-900 text-white":"text-gray-400 hover:bg-gray-100 hover:text-gray-700"}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-5 h-5"><path d={path}/></svg>
                </button>
              </Tip>
            ))}
            <div className="mt-auto pb-2">
              <button onClick={() => setRightOpen(o => !o)} className="w-9 h-9 flex items-center justify-center text-gray-400 hover:bg-gray-100 rounded-lg">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><polyline points={rightOpen?"9 18 15 12 9 6":"15 18 9 12 15 6"}/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>

       

      {captionPopup !== null && captionFrame && (
        <TextPopup value={captionFrame.caption} font={captionFont} size="14px" align={captionAlign}
          onSave={saveCaptionPopup} onCancel={() => setCaptionPopup(null)}/>
      )}


  {showOrganize && (
        <div className="fixed inset-0 bg-white z-50 flex flex-col">
          <div className="h-16 bg-white border-b border-gray-100 flex items-center justify-between px-4 sm:px-8 flex-shrink-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Organize pages</h2>
              <span className="hidden sm:inline text-gray-300">•</span>
              <p className="text-[12px] sm:text-[13px] text-gray-400">
                Add, rearrange, or delete pages.{lockedPageIds.size > 0 ? " Original pages can't be removed." : ""}
              </p>
            </div>
            <button onClick={() => setShowOrganize(false)} className="flex items-center gap-1.5 sm:gap-2 bg-gray-900 text-white text-[13px] sm:text-sm font-medium px-3.5 sm:px-4 py-2 rounded-full hover:bg-black transition-colors">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-3.5 h-3.5"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
              Close
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div className="flex flex-1 overflow-hidden flex-col md:flex-row bg-[#FAFAF9]">
         <div className="flex-1 overflow-y-auto p-5 sm:p-10" data-lenis-prevent>
              <div className="grid gap-x-5 gap-y-7" style={{ gridTemplateColumns:"repeat(auto-fill, minmax(120px, 1fr))" }}>

                {/* Cover thumbnail */}
                <div onClick={() => { setCur(0); setShowOrganize(false); }} className="flex flex-col items-center cursor-pointer group">
                  <div className={`w-full rounded-lg overflow-hidden flex bg-white transition-all ${cur===0 ? "ring-2 ring-gray-900" : "ring-1 ring-gray-200 group-hover:ring-gray-400"}`}
                    style={{ aspectRatio:"140/110", boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
                    <div className="flex-1 relative flex items-center justify-center" style={{ background: cur===0 ? "#8B96C4" : (pages[0].backBg || "#FFFDF7") }}>
                      {pages[0].backBgImage && <img src={pages[0].backBgImage} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                      <div className="absolute bottom-2 left-2 text-[5px] text-white/60 italic" style={{fontFamily:"serif", zIndex:2}}>Aishcraft</div>
                    </div>
                    <div style={{ width:1, background:"#eee" }}/>
                    <div className="flex-1 relative overflow-hidden flex items-center justify-center" style={{ background: pages[0].bg }}>
                      {pages[0].bgImage
                        ? <img src={pages[0].bgImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
                        : <svg viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.2" className="w-6 h-6"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>}
                      {pages[0].frames.map((f, fi) => (<div key={fi} className="absolute" style={{ left:`${f.x}%`, top:`${f.y}%`, width:`${f.w}%`, height:`${f.h}%`, background:f.image?`url(${f.image}) center/cover`:"transparent" }}/>))}
                    </div>
                  </div>
                  <div className="flex gap-3 mt-2">
                    <span className="text-[11px] text-gray-400">Back Cover</span>
                    <span className={`text-[11px] ${cur===0 ? "text-gray-900 font-semibold" : "text-blue-500 group-hover:text-blue-600"}`}>Front Cover</span>
                  </div>
                </div>

                {/* Normal page thumbnails */}
                {pages.slice(1).map((p, idx) => {
                  const pageIdx = idx + 1;
                  const isActive = cur === pageIdx;
                  return (
                    <div key={p.id} onClick={() => { setCur(pageIdx); setShowOrganize(false); }} className="flex flex-col items-center cursor-pointer group">
                      <div className={`w-full rounded-lg overflow-hidden relative bg-white flex items-center justify-center transition-all ${isActive ? "ring-2 ring-gray-900" : "ring-1 ring-gray-200 group-hover:ring-gray-400"}`}
                        style={{ aspectRatio:"110/140", boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
                        <div className="absolute" style={{ inset: "14%" }}>
                          {p.frames.length === 0 && (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.2" className="w-6 h-6"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
                            </div>
                          )}
                        </div>
                        {p.frames.map((f, fi) => (
                          <div key={fi} className="absolute rounded-[1px] flex items-center justify-center"
                            style={{ left:`${f.x}%`, top:`${f.y}%`, width:`${f.w}%`, height:`${f.h}%`, background:f.image?`url(${f.image}) center/cover`:"#E5E7EB" }}>
                            {!f.image && <svg viewBox="0 0 24 24" fill="none" stroke="#C7CBD1" strokeWidth="1.3" className="w-3 h-3"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>}
                          </div>
                        ))}
                      </div>
                      <span className={`text-[11px] mt-2 ${isActive ? "text-gray-900 font-semibold" : "text-blue-500 group-hover:text-blue-600"}`}>Page {pageIdx}</span>
                    </div>
                  );
                })}

                {/* Add page tile */}
                <div onClick={() => { addPage(); setShowOrganize(false); }} className="flex flex-col items-center cursor-pointer group">
                  <div className="w-full rounded-lg border-2 border-dashed border-gray-300 bg-white flex items-center justify-center group-hover:border-gray-500 group-hover:bg-gray-50 transition-colors"
                    style={{ aspectRatio:"110/140" }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5" className="w-7 h-7 group-hover:stroke-gray-600 transition-colors"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  </div>
                  <span className="text-[11px] text-gray-400 mt-2 group-hover:text-gray-600">Add Page</span>
                </div>
              </div>
            </div>

            {/* Right side action cards */}
        
<div className="w-full md:w-[260px] border-t md:border-t-0 md:border-l border-gray-100 bg-[#FAFAF9] p-3 flex flex-row md:flex-col gap-2 flex-shrink-0">
  <div
    onClick={cur !== 0 ? duplicatePage : undefined}
    className={`flex-1 md:w-full flex items-center justify-between px-4 py-3 rounded-xl border text-[13px] select-none
      ${cur === 0
        ? "bg-white border-gray-100 text-gray-300 cursor-not-allowed"
        : "bg-white border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300 cursor-pointer"}`}
  >
    <span>Duplicate page</span>
    <span className="flex items-center gap-1 bg-gray-100 rounded-md px-1.5 py-1">
      <span className="text-[10px]">0</span>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-2.5 h-2.5">
        <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
      </svg>
    </span>
  </div>

  <div
    onClick={!lockedPageIds.has(pages[cur]?.id) ? deletePage : undefined}
    className={`flex-1 md:w-full flex items-center justify-between px-4 py-3 rounded-xl border text-[13px] select-none
      ${lockedPageIds.has(pages[cur]?.id)
        ? "bg-white border-gray-100 text-gray-300 cursor-not-allowed"
        : "bg-white border-gray-200 text-gray-400 hover:text-red-400 hover:border-red-200 cursor-pointer"}`}
  >
    <span>{lockedPageIds.has(pages[cur]?.id) ? "Cannot remove page" : "Delete page"}</span>
    <span className="flex items-center gap-1 bg-gray-100 rounded-md px-1.5 py-1">
      <span className="text-[10px]">0</span>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-2.5 h-2.5">
        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
      </svg>
    </span>
  </div>
</div>
          </div>
        </div>
      )}
      {showSave && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setShowSave(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-[88vw] max-w-xs text-center relative" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowSave(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            <h3 className="text-base font-semibold text-gray-800 mb-6">Save Project</h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => setShowSave(false)} className="flex-1 py-2.5 border border-gray-300 text-gray-600 text-sm rounded-xl hover:bg-gray-50">Save & Exit ×</button>
              <button onClick={() => { setSaveStatus("saving"); setTimeout(() => setSaveStatus("saved"),1200); setShowSave(false); }} className="flex-1 py-2.5 bg-gray-800 text-white text-sm rounded-xl hover:bg-gray-900">Save & Continue 💾</button>
            </div>
          </div>
        </div>
      )}

      {showCheck && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setShowCheck(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-lg w-[90vw] mx-4 relative" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowCheck(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            <h3 className="text-base font-semibold text-gray-800 mb-3 text-center">Just checking...</h3>
            <p className="text-sm text-gray-500 text-center leading-relaxed mb-6">Your design is ready, but please take a moment to review your cover. Captions that haven't been edited won't be printed, so just double-check your cover-design and photo cropping before adding it to your cart. Ready to proceed?</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => setShowCheck(false)} className="px-6 py-2.5 border border-gray-300 text-gray-600 text-sm rounded-xl hover:bg-gray-50">Review and Fix ×</button>
              <button onClick={() => { setShowCheck(false); setShowCart(true); }} className="px-6 py-2.5 bg-gray-800 text-white text-sm rounded-xl hover:bg-gray-900">Continue Anyway ✓</button>
            </div>
          </div>
        </div>
      )}

      {showPreview && (
        <div className="fixed inset-0 bg-[#EDECEA] z-50 flex flex-col">
          <div className="h-[52px] bg-white border-b border-gray-200 flex items-center justify-between px-4 sm:px-6">
            <span className="text-sm font-semibold italic" style={{fontFamily:"Georgia,serif"}}>Aishcraft</span>
            <span className="hidden sm:inline text-sm text-gray-500">Photobook Editor</span>
            <button onClick={() => setShowPreview(false)} className="flex items-center gap-2 bg-gray-800 text-white text-[13px] sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-gray-900">▶ Close ×</button>
          </div>
          <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
          {isCover
  ? <div style={{ width:"min(92vw, 960px)", aspectRatio:"960 / 400", boxShadow:"0 8px 40px rgba(0,0,0,0.18)" }} className="rounded-sm">
      <CoverSpread page={page} readonly onPhotoClick={()=>{}} onCaptionClick={()=>{}} onFrameDrop={()=>{}}/>
    </div>
  : <div style={{ width:"min(92vw, 460px)", aspectRatio:"460 / 400", boxShadow:"0 8px 40px rgba(0,0,0,0.18)" }} className="rounded-sm">
      <PageCanvas page={page} readonly onPhotoClick={()=>{}} onCaptionClick={()=>{}} onFrameDrop={()=>{}}/>
    </div>
}
          </div>
          <div className="flex justify-center items-center gap-6 py-4">
            <button onClick={() => setCur(p => Math.max(0,p-1))} className="p-2 rounded-full bg-white shadow border border-gray-200 text-gray-500 hover:text-gray-800"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M15 18l-6-6 6-6"/></svg></button>
            <span className="text-xs text-gray-400">Page {cur+1} of {pages.length}</span>
            <button onClick={() => setCur(p => Math.min(pages.length-1,p+1))} className="p-2 rounded-full bg-white shadow border border-gray-200 text-gray-500 hover:text-gray-800"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M9 18l6-6-6-6"/></svg></button>
          </div>
        </div>
      )}
<div id="pdf-render-container" style={{ position: "fixed", top: 0, left: -99999, overflow: "visible" }} aria-hidden="true">
  {(pdfRenderPages || pages).map((pg) => (
    <div key={`pdf-${pg.id}`} style={{ width: pg.isCover ? 1600 : 800, height: pg.isCover ? 1131 : 1131, background: pg.bg }}>
      {renderPageForPdf(pg)}
    </div>
  ))}
</div>
    </div>
  );
}